import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { Card, Select, Input, Upload, Button, Space, Typography, Divider, message, Modal, Form, Steps, AutoComplete, Spin, Image, Checkbox, Radio } from 'antd';
import { PlusOutlined, ExclamationCircleFilled, UploadOutlined, CameraOutlined } from '@ant-design/icons';
import LoadingOverlay from '../LoadingOverlay';
import { convertHeicToJpegIfNeeded, compressToWebp } from '../../utils/imageUtils';
import { getCurrentLocation, isGeolocationSupported } from '../../utils/locationUtils';
import {
  getInspectionTemplateCompanies,
  getCompanyFleets,
  getCompanyFleetTrucks,
  deleteResponseImage,
  createInspectionResponse,
  updateInspectionResponse,
  uploadResponseImages,
  generateImageUploadUrls,
  confirmImageUploads,
  getInspectionResponseDetail,
  getAllVehicles,
  getInspectionToPdf
} from '../../utils/apiService';
import {
  generateNewInspectionDraftId,
  saveInspectionDraft,
  getInspectionDraft,
  deleteInspectionDraft,
} from '../../utils/inspectionCacheUtils';
import SignatureModalPad from './InspectionFormBuilderComponents/signatureModalPad.jsx';

// Limit concurrent image compressions to avoid OOM on mobile (2 canvas decodes at a time)
const COMPRESSION_CONCURRENCY = 2;
// Show full-screen overlay when compressing this many or more images at once
const BULK_COMPRESSION_OVERLAY_THRESHOLD = 3;
const runWithConcurrency = async (tasks, limit) => {
  const running = new Set();
  for (const task of tasks) {
    const p = task().finally(() => running.delete(p));
    running.add(p);
    if (running.size >= limit) await Promise.race(running);
  }
  await Promise.all(running);
};

const fieldStyles = {
  '.ant-form-item-has-error .ant-input, .ant-form-item-has-error .ant-select-selector, .ant-form-item-has-error .ant-picker': {
    borderColor: '#ff4d4f',
  },
  '.ant-form-item-explain-error': {
    color: '#ff4d4f',
  },
  '.field-error': {
    borderLeft: '3px solid #ff4d4f',
    paddingLeft: '12px',
    marginBottom: '16px',
  },
};

const { TextArea } = Input;
const { Title, Text } = Typography;
const handleRemove = async (file) => {
  try {
    // If it's a server file (already uploaded), call your API endpoint
    const canDeleteFromServer = !!(file?.blobPath && file?.blobContainer);
    if (file.isServer || canDeleteFromServer) {
      if (!canDeleteFromServer) {
        message.error('Failed to delete image from server. Missing blob reference.');
        return false;
      }

      const response = await deleteResponseImage({
        blobPath: file.blobPath,
        blobContainer: file.blobContainer
      });

      if (response) {
        message.success('Image deleted successfully from server.');
      } else {
        message.error('Failed to delete image from server.');
        return false; // Prevent UI removal
      }
    } else {
      // If it's a local file (not uploaded yet), remove it from local storage
      const savedFiles = JSON.parse(localStorage.getItem('uploadedFiles') || '[]');
      const updatedFiles = savedFiles.filter(f => f.uid !== file.uid);
      localStorage.setItem('uploadedFiles', JSON.stringify(updatedFiles));
      message.success('Image removed from local storage.');
    }

    // Allow AntD to remove from UI
    return true;
  } catch (err) {
    message.error('An error occurred while deleting the image');
    return false; // Prevent AntD from removing file from UI
  }
};
const safeParse = (val) => {
  if (typeof val !== 'string') return null;
  try { return JSON.parse(val); } catch { return null; }
};

const deepClone = (obj) => {
  try { return JSON.parse(JSON.stringify(obj)); } catch { return obj; }
};

const stripFleetInfoBloat = (fi) => {
  if (!fi || typeof fi !== 'object') return fi;
  const { _original, name, ...clean } = fi;
  return clean;
};

const stripClientPropsForSave = (json) => {
  const copy = deepClone(json);
  if (copy && Array.isArray(copy.templates)) {
    copy.templates = copy.templates.map((t) => {
      const next = { ...t, response: { ...(t.response || {}) } };
      if ('__serverImageCount' in next.response) {
        delete next.response.__serverImageCount;
      }

      const stripField = (arr) => (arr || []).map((f) => {
        const r = { ...(f.response || {}) };
        if ('__serverImageCount' in r) {
          delete r.__serverImageCount;
        }
        if (r.fleetInfo) {
          r.fleetInfo = stripFleetInfoBloat(r.fleetInfo);
        }
        return { ...f, response: r };
      });

      next.FleetInformation = stripField(t.FleetInformation);
      next.InspectionQuestions = stripField(t.InspectionQuestions);
      return next;
    });
  }
  return copy;
};

const applyServerImageCounts = (json, images) => {
  const byKey = (images || []).reduce((acc, it) => {
    const key = it?.fieldKey || it?.itemId;
    if (!key) return acc;
    let count = 0;
    if (Array.isArray(it.imagesBase64)) count = it.imagesBase64.length;
    else if (Array.isArray(it.images)) count = it.images.length;
    else if (typeof it.count === 'number') count = it.count;
    acc[key] = (acc[key] || 0) + count;
    return acc;
  }, {});

  const base = deepClone(json) || {};

  base.templates = (base.templates || []).map((tpl) => {
    const annotate = (arr) => (arr || []).map((field) => ({
      ...field,
      response: {
        ...(field.response || {}),
        __serverImageCount: byKey[field.itemId] || 0,
      },
    }));

    return {
      ...tpl,
      FleetInformation: annotate(tpl.FleetInformation),
      InspectionQuestions: annotate(tpl.InspectionQuestions),
    };
  });

  return base;
};

const mimeToExt = (mime) => {
  const imageData = mime.split(',')[0];
  if (!imageData) return 'png';
  if (imageData.includes('png')) return 'png';
  if (imageData.includes('jpeg') || imageData.includes('jpg')) return 'jpg';
  if (imageData.includes('webp')) return 'webp';
  if (imageData.includes('gif')) return 'gif';
  return 'png';
};

const stableHash = (input) => {
  const str = String(input ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

const fileSignature = (f) => {
  try {
    if (!f) return 'null';
    const isServer = !!f.isServer;
    const blobPath = typeof f.blobPath === 'string' ? f.blobPath : '';
    const blobContainer = typeof f.blobContainer === 'string' ? f.blobContainer : '';
    const url = typeof f.url === 'string' ? f.url : '';
    const thumbUrl = typeof f.thumbUrl === 'string' ? f.thumbUrl : '';
    const uid = typeof f.uid === 'string' ? f.uid : '';

    // IMPORTANT: Server image URLs can change between saves (SAS tokens, CDN URLs, etc.).
    // Use stable identifiers (blobPath / uid) first for server images so dedupe remains stable.
    const primary = blobPath || (isServer ? uid : (url || thumbUrl || uid));
    return `${isServer ? 'S' : 'L'}|${blobContainer}|${primary}`;
  } catch {
    return String(f?.uid ?? f?.url ?? 'unknown');
  }
};

const dedupeUploadList = (list) => {
  const seen = new Set();
  return (list || []).filter((f) => {
    const sig = fileSignature(f);
    if (seen.has(sig)) return false;
    seen.add(sig);
    return true;
  });
};

// Merge server and local image lists, preferring local blobs over server refs
const mergeImageLists = (serverList, localList) => {
  const server = serverList || [];
  const local = localList || [];

  // Build a map of blobPath -> local file for quick lookup
  const localByBlobPath = new Map();
  const localByUid = new Map();

  local.forEach((f) => {
    if (f?.blobPath) {
      localByBlobPath.set(f.blobPath, f);
    }
    if (f?.uid) {
      localByUid.set(f.uid, f);
    }
  });

  // Start with local files (they have blobs for offline use)
  const merged = [...local];

  // Add server files only if we don't already have them as local blobs
  server.forEach((serverFile) => {
    // Skip if we have this server image as a local blob
    if (serverFile?.blobPath && localByBlobPath.has(serverFile.blobPath)) {
      return;
    }
    // Skip if we have a prefetched version (uid match)
    if (serverFile?.uid && localByUid.has(`prefetch-${serverFile.uid}`)) {
      return;
    }
    // Add this server image since we don't have it locally
    merged.push(serverFile);
  });

  return dedupeUploadList(merged);
};

const itemRequiresImage = (item) => item?.requiresImage === true;

const hasItemImages = (item, filesMap, serverFileMap, responseOverride) => {
  const itemId = item?.itemId || item?.id;
  if (!itemId) return false;

  const response = responseOverride || item?.response || {};
  return (
    (Array.isArray(response.imageIds) && response.imageIds.length > 0) ||
    mergeImageLists(serverFileMap?.[itemId], filesMap?.[itemId]).length > 0 ||
    (typeof response.__serverImageCount === 'number' && response.__serverImageCount > 0)
  );
};

const imagesArrayToUploadMap = (images) => {
  if (!Array.isArray(images)) return {};

  const seenByKey = {};
  const ensureSeen = (k) => {
    if (!seenByKey[k]) seenByKey[k] = new Set();
    return seenByKey[k];
  };
  const tryPush = (acc, key, entry) => {
    if (!key || !entry) return;
    if (!acc[key]) acc[key] = [];
    const sig = fileSignature(entry);
    const seen = ensureSeen(key);
    if (seen.has(sig)) return;
    seen.add(sig);
    acc[key].push(entry);
  };

  return images.reduce((acc, img) => {
    if (img?.itemId && (img.url || img.thumbnailUrl)) {
      const uidSeed = img.id || img.blobPath || img.url || img.thumbnailUrl || `${img.itemId}-${img.name || ''}`;
      tryPush(acc, img.itemId, {
        uid: `server-${img.itemId}-${stableHash(uidSeed)}`,
        name: img.name || 'image.jpg',
        status: 'done',
        url: img.url || img.thumbnailUrl,
        isServer: true,
        blobPath: Array.isArray(img.blobPath) ? img.blobPath[0] : img.blobPath,
        blobContainer: Array.isArray(img.blobContainer) ? img.blobContainer[0] : img.blobContainer,
      });
    }

    const fieldKey = img?.fieldKey || img?.itemId;
    if (fieldKey && Array.isArray(img?.imageUrls) && img.imageUrls.length > 0) {
      img.imageUrls.forEach((url, idx) => {
        const ext = String(url || '').split('?')[0].split('.').pop() || 'webp';
        tryPush(acc, fieldKey, {
          uid: `server-${fieldKey}-${idx}`,
          name: `image-${idx + 1}.${ext}`,
          status: 'done',
          url,
          isServer: true,
          blobPath: img.blobPath?.[idx],
          blobContainer: img.blobContainer?.[idx],
        });
      });
    } else if (fieldKey && Array.isArray(img?.imagesBase64) && img.imagesBase64.length > 0) {
      img.imagesBase64.forEach((dataUrl, idx) => {
        const ext = mimeToExt(String(dataUrl || '').toLowerCase());
        tryPush(acc, fieldKey, {
          uid: `server-${fieldKey}-${idx}`,
          name: `image-${idx + 1}.${ext}`,
          status: 'done',
          url: dataUrl,
          isServer: true,
          blobPath: img.blobPath?.[idx],
          blobContainer: img.blobContainer?.[idx],
        });
      });
    }

    return acc;
  }, {});
};

const DropdownOption = ({ option, showLabel = true }) => {
  if (!option) return null;

  // Only use the color specified in the option
  const color = option.color || option.colour || option.Color || 'gray'; // Default to gray if no color is specified

  return (
    <Space>
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: color,
          border: '1px solid #d9d9d9',
          flexShrink: 0
        }}
      />
      {showLabel && <span style={{ marginLeft: 8 }}>{option.name || option.Name || option.label || option.Label || option.id || option.Id}</span>}
    </Space>
  );
};

const normalizeListOption = (option) => {
  const id = option?.id ?? option?.Id ?? option?.value ?? option?.Value ?? option?.optionId ?? option?.OptionId;
  const label = option?.name ?? option?.Name ?? option?.label ?? option?.Label ?? option?.text ?? option?.Text ?? String(id ?? '');
  const color = String(option?.color ?? option?.colour ?? option?.Color ?? option?.Colour ?? '').toLowerCase();
  return { id, label: String(label), color };
};

const getChecklistResult = (options, optionIds, skipped = false) => {
  if (skipped) {
    return { color: null, checkedCount: 0, uncheckedCount: 0 };
  }

  const selectedIds = new Set((optionIds || []).map(value => String(value)));
  const checkedCount = (options || []).filter(option =>
    selectedIds.has(String(option.id))
  ).length;
  const uncheckedCount = Math.max(0, (options || []).length - checkedCount);
  return {
    color: uncheckedCount > 0 ? 'red' : 'green',
    checkedCount,
    uncheckedCount,
  };
};

const ColorBadge = ({ color, size = 20, shape = 'circle', selected = false, hasSelection = false }) => {
  const map = { red: '#ff4d4f', amber: '#faad14', green: '#52c41a' };
  let bg = map[color] || '#d9d9d9';

  // Add 20% opacity to unselected badges only if a selection has been made
  if (!selected && hasSelection) {
    bg = `${bg}33`;
  }

  const borderColor = selected ? '#1677ff' : 'transparent';

  return (
    <div
      aria-label={color}
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        background: bg,
        borderRadius: shape === 'circle' ? '50%' : '999px',
        border: `2px solid ${borderColor}`,
        transition: 'opacity 0.2s',
      }}
    />
  );
};

const BadgeSelector = ({ value, onChange, enumColors = ['red', 'amber', 'green'], editable = true }) => (
  <div role="radiogroup" aria-label="Select badge color">
    <Space wrap>
      {enumColors.map((c) => {
        const selected = value === c;
        return (
          <Button
            key={c}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange && onChange(c)}
            style={{
              border: 'none',
              padding: '0px',
              margin: '8px 10px 0px 5px',
              outline: 'none',
            }}
            disabled={!editable}
          >
            <ColorBadge
              color={c}
              size={48}
              shape="circle"
              selected={selected}
              hasSelection={value !== undefined && value !== null && value !== ''}
            />
          </Button>
        );
      })}
    </Space>
  </div>
);

const { Option } = Select;


const InspectionFormBuilder = ({
  templateJson,
  setTemplateJson,
  templateId,
  responseId,
  serverImages,
  companyId: propCompanyId,
  reference,
  onSaved,
  editable = true,
  draftKey: propDraftKey,
}) => {
  const [filesMap, setFilesMap] = useState({}); // local files selected this session: { [itemId]: UploadFile[] }
  const [processingImages, setProcessingImages] = useState(false);
  const [compressingUids, setCompressingUids] = useState(new Set()); // UIDs currently being compressed
  const bulkTotalRef = useRef(0); // total images queued across all batches (for overlay text)
  const [bulkCompressionProgress, setBulkCompressionProgress] = useState({ total: 0, done: 0 }); // for overlay text
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const handlePreview = async (file) => {
    let src = file.url || file.thumbUrl;
    if (!src && file.originFileObj) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    if (src) {
      setPreviewImage(src);
      setPreviewOpen(true);
    }
  };

  // State for upload summary modal
  const [uploadSummaryModal, setUploadSummaryModal] = useState({ visible: false, valid: [], invalid: [] });
  const [serverFileMap, setServerFileMap] = useState({}); // server previews per itemId
  const [submitting, setSubmitting] = useState(false);
  const [isSubmittingDraft, setIsSubmittingDraft] = useState(false);
  const [isSubmittingCompleted, setIsSubmittingCompleted] = useState(false);
  const [badgeModalVisible, setBadgeModalVisible] = useState(false);
  const [currentBadgeItem, setCurrentBadgeItem] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState(null);

  const selectedCompanyRef = useRef(selectedCompany);
  const selectedCompanyNameRef = useRef(selectedCompanyName);
  useEffect(() => {
    selectedCompanyRef.current = selectedCompany;
  }, [selectedCompany]);
  useEffect(() => {
    selectedCompanyNameRef.current = selectedCompanyName;
  }, [selectedCompanyName]);

  const skipDraftSaveOnUnmountRef = useRef(false);
  const draftFinalizedRef = useRef(false);
  const hasLocalDraftRef = useRef(false);
  const pdfFiredIdsRef = useRef(new Set());
  const [fleets, setFleets] = useState([]);
  const [selectedFleetId, setSelectedFleetId] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [currentStep, setCurrentStep] = useState(0); // Track current form step (0 for fleet info, 1 for inspection)
  const [form] = Form.useForm();
  const [invalidFields, setInvalidFields] = useState([]);
  const [isManualVehicleSelect, setIsManualVehicleSelect] = useState(false); // Track if vehicle was selected manually
  const [updateKey, setUpdateKey] = useState(0); // Track form updates
  const [loadingFleets, setLoadingFleets] = useState(false); // Loading state for fleets
  const [fleetPageIndex, setFleetPageIndex] = useState(0); // Current page index for fleet pagination
  const [fleetHasMore, setFleetHasMore] = useState(false); // Whether more fleet pages are available
  const [fleetSearchTerm, setFleetSearchTerm] = useState(''); // Search term for fleet server-side search
  const [fleetTotalCount, setFleetTotalCount] = useState(0); // Total fleet count from server
  const FLEET_PAGE_SIZE = 20; // Page size for fleet infinite scroll
  const VEHICLE_PAGE_SIZE = 20; // Page size for vehicle search infinite scroll
  const [vehiclePageIndex, setVehiclePageIndex] = useState(0);
  const [vehicleHasMore, setVehicleHasMore] = useState(false);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [badgeForm] = Form.useForm();
  const badgeCommentValue = Form.useWatch('comment', badgeForm);

  const [draftId, setDraftId] = useState(() => {
    if (responseId && propDraftKey) return propDraftKey;
    if (!responseId) return propDraftKey || generateNewInspectionDraftId();
    return null; // Don't create draft for completed inspections
  });
  const [draftCacheKey, setDraftCacheKey] = useState(() => draftId);
  const [loadedResponseId, setLoadedResponseId] = useState(null); // responseId from loaded local draft
  const originalDraftKeyRef = useRef(draftId);
  const draftCreatedOnRef = useRef(Date.now());
  const [draftInitComplete, setDraftInitComplete] = useState(false);
  const serverImagesPrefetchedRef = useRef(false);

  const navigate = useNavigate();

  // Extract fleet information and inspection questions from templates
  const { fleetInformation = [], inspectionQuestions = [] } = useMemo(() => {
    if (!templateJson?.templates?.[0]) return {};

    const template = templateJson.templates[0];
    return {
      fleetInformation: template.FleetInformation || [],
      inspectionQuestions: template.InspectionQuestions || []
    };
  }, [templateJson]);

  const templateRef = useRef(templateJson);
  useEffect(() => {
    templateRef.current = templateJson;
  }, [templateJson]);

  // Stable draft key: use propDraftKey if resuming, or generate once per mount
  const stableDraftKeyRef = useRef(draftId);
  useEffect(() => {
    if (stableDraftKeyRef.current) return;
    // For server drafts, link local draft to responseId; for new inspections use propDraftKey or generate new
    if (responseId && propDraftKey) {
      stableDraftKeyRef.current = propDraftKey;
    } else if (!responseId) {
      stableDraftKeyRef.current = propDraftKey || generateNewInspectionDraftId();
    } else {
      // Don't create draft for completed inspections (responseId exists but no propDraftKey)
      stableDraftKeyRef.current = null;
    }
    if (!originalDraftKeyRef.current) {
      originalDraftKeyRef.current = stableDraftKeyRef.current;
    }
    setDraftId(stableDraftKeyRef.current);
    setDraftCacheKey(stableDraftKeyRef.current);
  }, [responseId, propDraftKey]);

  // Track whether we've already loaded from local draft to avoid re-applying
  const localDraftLoadedRef = useRef(false);
  // Prevent auto-saves while draft is loading
  const draftLoadingRef = useRef(false);

  // Load existing local draft (if any) when starting or resuming an inspection
  // Use draftCacheKey in dependency (state) instead of ref to properly trigger re-runs
  useEffect(() => {
    if (!templateId && !responseId) return;
    if (!draftCacheKey) return; // Wait until draft key is set
    if (localDraftLoadedRef.current) return; // Already loaded once

    setDraftInitComplete(false);

    const init = async () => {
      draftLoadingRef.current = true;
      try {
        const key = draftCacheKey;
        const existing = await getInspectionDraft(key);
        if (existing && existing.data) {
          if (existing.createdOn) {
            draftCreatedOnRef.current = existing.createdOn;
          }
          const persistedOriginalKey = existing.originalDraftKey || existing.data?.originalDraftKey;
          if (persistedOriginalKey) {
            originalDraftKeyRef.current = persistedOriginalKey;
          }
          localDraftLoadedRef.current = true;
          hasLocalDraftRef.current = true;
          const data = existing.data;
          const draftTpl = data.templateJson;
          const hasValidTemplate =
            draftTpl &&
            Array.isArray(draftTpl.templates) &&
            draftTpl.templates.length > 0;

          // Apply local draft template whenever it is valid (it contains the user's latest saved state)
          if (hasValidTemplate) {
            setTemplateJson(draftTpl);
            templateRef.current = draftTpl;
          }
          const persistedCompanyId = data.selectedCompany || data.companyId || existing.companyId || existing.data?.companyId;
          const persistedCompanyName = data.companyName || existing.companyName || existing.data?.companyName;
          if (persistedCompanyId) {
            selectedCompanyRef.current = persistedCompanyId;
            setSelectedCompany(persistedCompanyId);
          }
          if (persistedCompanyName) {
            selectedCompanyNameRef.current = persistedCompanyName;
            setSelectedCompanyName(persistedCompanyName);
          }
          if (data.selectedVehicle) setSelectedVehicle(data.selectedVehicle);
          setCurrentStep(0);
          const persistedServerFileMap = data.serverFileMap || {};

          // Restore responseId if draft was linked to a server draft
          if (existing.responseId) {
            setLoadedResponseId(existing.responseId);
          }

          // Rehydrate image lists from serialized localImages, preserving isServer flag
          if (data.localImages && Object.keys(data.localImages).length > 0) {
            const restoredLocal = {};
            const restoredServer = {};
            const seenSigs = new Set();
            Object.entries(data.localImages).forEach(([itemId, list]) => {
              (list || []).forEach((img) => {
                if (!img?.uid) return;
                const normalizedIsServer = !!img.isServer;

                const resolvedUrl = (() => {
                  const u = typeof img.url === 'string' ? img.url : '';
                  const t = typeof img.thumbUrl === 'string' ? img.thumbUrl : '';
                  const candidate = u || t;
                  if (candidate && !candidate.startsWith('blob:')) return candidate;
                  if (!normalizedIsServer && img.blob && typeof URL !== 'undefined' && URL.createObjectURL) {
                    try {
                      return URL.createObjectURL(img.blob);
                    } catch {
                      return candidate || '';
                    }
                  }
                  return candidate || '';
                })();

                const entry = {
                  uid: img.uid,
                  name: img.name,
                  status: img.status || 'done',
                  type: img.type,
                  thumbUrl: resolvedUrl || img.thumbUrl,
                  url: resolvedUrl || img.url || img.thumbUrl,
                  isServer: normalizedIsServer,
                  blob: !normalizedIsServer ? (img.blob || null) : null,
                  originFileObj: !normalizedIsServer ? (img.blob || null) : null,
                  blobPath: img.blobPath,
                  blobContainer: img.blobContainer,
                };

                const sig = fileSignature(entry);
                if (seenSigs.has(sig)) return;
                seenSigs.add(sig);

                if (entry.isServer) {
                  restoredServer[itemId] = restoredServer[itemId] || [];
                  restoredServer[itemId].push(entry);
                } else {
                  restoredLocal[itemId] = restoredLocal[itemId] || [];
                  restoredLocal[itemId].push(entry);
                }
              });
            });
            Object.entries(restoredLocal).forEach(([k, list]) => {
              restoredLocal[k] = dedupeUploadList((list || []).map((f) => ({ ...f, isServer: false })));
            });
            Object.entries(restoredServer).forEach(([k, list]) => {
              restoredServer[k] = dedupeUploadList((list || []).map((f) => ({ ...f, isServer: true })));
            });
            setFilesMap(restoredLocal);
            const merged = {};
            Object.entries(persistedServerFileMap || {}).forEach(([itemId, list]) => {
              merged[itemId] = dedupeUploadList((list || []).map((f) => ({ ...f, isServer: true })));
            });
            Object.entries(restoredServer).forEach(([itemId, list]) => {
              merged[itemId] = dedupeUploadList([...(merged[itemId] || []), ...((list || []).map((f) => ({ ...f, isServer: true })))]);
            });
            setServerFileMap(merged);
          } else if (data.filesMap && Object.keys(data.filesMap).length > 0) {
            // Backwards compatibility with older drafts that stored raw filesMap
            const legacyLocal = {};
            const legacyServer = {};
            Object.entries(data.filesMap || {}).forEach(([itemId, list]) => {
              (list || []).forEach((f) => {
                if (!f?.uid) return;
                const normalizedIsServer = !!f.isServer;
                const entry = {
                  uid: f.uid,
                  name: f.name,
                  status: f.status || 'done',
                  type: f.type,
                  thumbUrl: f.thumbUrl,
                  url: f.url || f.thumbUrl,
                  isServer: normalizedIsServer,
                  blob: !normalizedIsServer ? (f.originFileObj || f.blob || null) : null,
                  originFileObj: !normalizedIsServer ? (f.originFileObj || f.blob || null) : null,
                  blobPath: f.blobPath,
                  blobContainer: f.blobContainer,
                };
                if (entry.isServer) {
                  legacyServer[itemId] = legacyServer[itemId] || [];
                  legacyServer[itemId].push(entry);
                } else {
                  legacyLocal[itemId] = legacyLocal[itemId] || [];
                  legacyLocal[itemId].push(entry);
                }
              });
            });
            Object.entries(legacyLocal).forEach(([k, list]) => {
              legacyLocal[k] = dedupeUploadList((list || []).map((f) => ({ ...f, isServer: false })));
            });
            Object.entries(legacyServer).forEach(([k, list]) => {
              legacyServer[k] = dedupeUploadList((list || []).map((f) => ({ ...f, isServer: true })));
            });
            const merged = {};
            Object.entries(persistedServerFileMap || {}).forEach(([itemId, list]) => {
              merged[itemId] = dedupeUploadList((list || []).map((f) => ({ ...f, isServer: true })));
            });
            Object.entries(legacyServer).forEach(([itemId, list]) => {
              merged[itemId] = dedupeUploadList([...(merged[itemId] || []), ...((list || []).map((f) => ({ ...f, isServer: true })))]);
            });
            setFilesMap(legacyLocal);
            setServerFileMap(merged);
          } else {
            const merged = {};
            Object.entries(persistedServerFileMap || {}).forEach(([itemId, list]) => {
              merged[itemId] = dedupeUploadList((list || []).map((f) => ({ ...f, isServer: true })));
            });
            setServerFileMap(merged);
          }
        }
      } catch (e) {
        console.error('Failed to load local inspection draft:', e);
      } finally {
        draftLoadingRef.current = false;
        setDraftInitComplete(true);
      }
    };

    init();
  }, [templateId, responseId, draftCacheKey]);

  useEffect(() => {
    return () => {
      if (skipDraftSaveOnUnmountRef.current) return;
      try {
        debouncedSaveDraft.flush?.();
      } catch { }
      const snapshot = buildDraftSnapshotRef.current();
      if (snapshot) {
        Promise.resolve(saveInspectionDraft(snapshot))
          .then((ok) => {
            if (!ok) {
              console.error('Failed to save local inspection draft on unmount:', { cacheKey: snapshot.cacheKey });
            }
          })
          .catch((e) => {
            console.error('Failed to save local inspection draft on unmount:', e);
          });
      }
    };
  }, []);

  // Revoke all blob preview URLs on unmount to free memory
  useEffect(() => {
    return () => {
      for (const url of objectUrlsRef.current.values()) {
        try { URL.revokeObjectURL(url); } catch { }
      }
      objectUrlsRef.current.clear();
    };
  }, []);

  const buildDraftSnapshot = () => {
    const id = stableDraftKeyRef.current || draftId || draftCacheKey || propDraftKey;
    const cacheKey = id;
    if (!id || !cacheKey) {
      return null;
    }

    // Do not persist a draft until the template has actually loaded
    const tpl = templateRef.current || templateJson;
    if (!tpl || !tpl.templates || !Array.isArray(tpl.templates) || !tpl.templates[0]) {
      return null;
    }

    const primaryTemplate = tpl.templates[0];
    const templateName = tpl?.inspectionName
      || tpl?.name
      || tpl?.templateName
      || tpl?.InspectionName
      || tpl?.TemplateName
      || primaryTemplate?.inspectionName
      || primaryTemplate?.templateName
      || primaryTemplate?.InspectionName
      || primaryTemplate?.TemplateName
      || primaryTemplate?.name
      || '';

    const extractCompanyFromTemplateJson = (t) => {
      try {
        const fleetInfo = t?.templates?.[0]?.FleetInformation;
        if (!Array.isArray(fleetInfo)) return '';
        const patterns = ['company', 'client', 'customer', 'account'];
        const field = fleetInfo.find((f) => {
          const title = String(f?.title || '').toLowerCase();
          return patterns.some((p) => title.includes(p));
        });
        const txt = field?.response?.text;
        return txt ? String(txt).trim() : '';
      } catch {
        return '';
      }
    };

    const hasResponsesIn = (arr) => {
      return (arr || []).some((f) => {
        const r = f?.response || {};
        const text = r?.text;
        const comment = r?.comment;
        const optionId = r?.optionId;
        const optionIds = r?.optionIds;
        const color = r?.color;
        if (typeof text === 'string' && text.trim().length > 0) return true;
        if (typeof comment === 'string' && comment.trim().length > 0) return true;
        if (optionId !== undefined && optionId !== null && String(optionId).trim().length > 0) return true;
        if (Array.isArray(optionIds) && optionIds.length > 0) return true;
        if (r?.skipped === true) return true;
        if (typeof color === 'string' && color.trim().length > 0) return true;
        return false;
      });
    };

    const tpl0 = tpl.templates[0] || {};
    const hasVehicle = !!(selectedVehicle?.registration || selectedVehicle?.fleet || selectedVehicle?.vin);
    const hasImages =
      Object.values(filesMap || {}).some((list) => (list || []).length > 0) ||
      Object.values(serverFileMap || {}).some((list) => (list || []).length > 0);
    const hasResponses = hasResponsesIn(tpl0.FleetInformation) || hasResponsesIn(tpl0.InspectionQuestions);
    if (!hasVehicle && !hasImages && !hasResponses) {
      return null;
    }

    // Serialize all images into a JSON-safe structure for offline drafts
    // Combine filesMap (local + rehydrated) and serverFileMap, deduplicating by uid
    const localImages = {};
    const seenSigs = new Set();

    const stripHeavyLocalUrls = (entry) => {
      if (!entry || entry.isServer) return entry;
      if (!entry.blob) return entry;
      const url = typeof entry.url === 'string' ? entry.url : '';
      const thumbUrl = typeof entry.thumbUrl === 'string' ? entry.thumbUrl : '';
      // If we already have a Blob/File to persist in IndexedDB, do not also store huge base64 strings.
      // This prevents exceeding IndexedDB quota and losing images while the rest of the draft saves.
      return {
        ...entry,
        url: url.startsWith('data:') ? '' : url,
        thumbUrl: thumbUrl.startsWith('data:') ? '' : thumbUrl,
      };
    };

    const toSafeUploadEntry = (f, forceIsServer) => {
      if (!f) return null;
      const isServer = (typeof forceIsServer === 'boolean') ? forceIsServer : !!f.isServer;
      const entry = {
        uid: f.uid,
        name: f.name,
        status: f.status || 'done',
        type: f.type,
        thumbUrl: f.thumbUrl,
        url: f.url || f.thumbUrl,
        isServer,
        blob: !isServer ? (f.originFileObj || f.blob || null) : null,
        blobPath: f.blobPath,
        blobContainer: f.blobContainer,
      };
      return stripHeavyLocalUrls(entry);
    };

    // First add all images from filesMap (includes both local and previously loaded)
    Object.entries(filesMap || {}).forEach(([itemId, list]) => {
      if (!list || !list.length) return;
      localImages[itemId] = list
        .filter((f) => {
          const sig = fileSignature(f);
          if (seenSigs.has(sig)) return false;
          seenSigs.add(sig);
          return true;
        })
        .map((f) => stripHeavyLocalUrls({
          uid: f.uid,
          name: f.name,
          type: f.type,
          status: f.status,
          thumbUrl: f.thumbUrl,
          url: f.url || f.thumbUrl,
          isServer: !!f?.isServer, // Respect the actual isServer flag, not blobPath presence
          blob: !f?.isServer ? (f.originFileObj || f.blob || null) : null,
          blobPath: f.blobPath,
          blobContainer: f.blobContainer,
        }));
    });

    // Then add any server images not already included
    Object.entries(serverFileMap || {}).forEach(([itemId, list]) => {
      if (!list || !list.length) return;
      const existing = localImages[itemId] || [];
      const newServerImages = list
        .filter((f) => {
          const sig = fileSignature({ ...f, isServer: true });
          if (seenSigs.has(sig)) return false;
          seenSigs.add(sig);
          return true;
        })
        .map((f) => ({
          uid: f.uid,
          name: f.name,
          type: f.type,
          status: f.status,
          thumbUrl: f.thumbUrl,
          url: f.url || f.thumbUrl,
          isServer: true,
          blobPath: f.blobPath,
          blobContainer: f.blobContainer,
        }));
      if (newServerImages.length > 0) {
        localImages[itemId] = [...existing, ...newServerImages];
      }
    });

    const companyIdToPersist = selectedCompanyRef.current || propCompanyId || '';
    const companyFromList = companyIdToPersist
      ? companies.find(c => c.id === companyIdToPersist || c.id === String(companyIdToPersist))
      : null;
    const companyName = selectedCompanyNameRef.current || companyFromList?.name || extractCompanyFromTemplateJson(tpl) || null;

    const safeFilesMap = {};
    Object.entries(filesMap || {}).forEach(([itemId, list]) => {
      safeFilesMap[itemId] = (list || []).map((f) => toSafeUploadEntry(f, !!f?.isServer || !!f?.blobPath || !!f?.blobContainer)).filter(Boolean);
    });

    const safeServerFileMap = {};
    Object.entries(serverFileMap || {}).forEach(([itemId, list]) => {
      safeServerFileMap[itemId] = (list || []).map((f) => toSafeUploadEntry(f, true)).filter(Boolean);
    });

    return {
      draftId: id,
      cacheKey,
      templateId,
      companyId: companyIdToPersist,
      companyName: companyName || '', // Persist selected company name
      originalDraftKey: originalDraftKeyRef.current || '',
      responseId: responseId || loadedResponseId || null,
      createdOn: draftCreatedOnRef.current,
      data: {
        templateJson: tpl,
        templateName,
        selectedCompany: companyIdToPersist,
        companyId: companyIdToPersist,
        companyName,
        originalDraftKey: originalDraftKeyRef.current || '',
        selectedVehicle,
        currentStep: 0,
        filesMap: safeFilesMap,
        serverFileMap: safeServerFileMap,
        localImages,
      },
    };
  };

  // Keep buildDraftSnapshot in a ref to avoid stale closure in debounced function
  const buildDraftSnapshotRef = useRef(buildDraftSnapshot);
  useEffect(() => {
    buildDraftSnapshotRef.current = buildDraftSnapshot;
  });

  const debouncedSaveDraft = useRef(
    debounce(async () => {
      if (!hasLocalDraftRef.current) {
        const snapshot = buildDraftSnapshotRef.current();
        if (snapshot) {
          const ok = await saveInspectionDraft(snapshot);
          hasLocalDraftRef.current = true;
          return;
        }
      }
      if (draftFinalizedRef.current) {
        return;
      }
      // Don't save while loading a draft to avoid overwriting loaded data
      if (draftLoadingRef.current) {
        return;
      }
      const snapshot = buildDraftSnapshotRef.current();
      if (!snapshot) return;
      const ok = await saveInspectionDraft(snapshot);
      // If submission completed while this save was in-flight, remove the draft immediately.
      // This prevents an in-flight autosave from re-creating the draft after we delete it.
      if (draftFinalizedRef.current && snapshot?.cacheKey) {
        try {
          const deleted = await deleteInspectionDraft(snapshot.cacheKey);
        } catch (e) {
          console.error('Failed to delete in-flight autosave draft after finalize:', {
            cacheKey: snapshot.cacheKey,
            message: e?.message,
          });
        }
        return;
      }
      if (!ok) {
        console.error('Failed to auto-save inspection draft locally:', { cacheKey: snapshot.cacheKey });
      }
    }, 2000)
  ).current;

  useEffect(() => {
    return () => {
      try {
        debouncedSaveDraft.flush?.();
        debouncedSaveDraft.cancel?.();
      } catch { }
    };
  }, [debouncedSaveDraft]);

  const removeInvalid = useCallback((fieldId) => {
    setInvalidFields((prev) => prev.filter((id) => id !== fieldId));
  }, []);

  useEffect(() => {
    let style = document.getElementById('inspection-form-actions-styles');
    if (!style) {
      style = document.createElement('style');
      style.id = 'inspection-form-actions-styles';
      document.head.appendChild(style);
    }
    style.textContent = `
      .inspection-actions { display: flex; justify-content: flex-start; width: 100%; margin-top: 16px; margin-bottom: calc(env(safe-area-inset-bottom) + 2px); gap: 8px; align-items: center; flex-wrap: nowrap; position: relative; }
      .inspection-actions-left { display: flex; }
      .inspection-actions-right { display: inline-flex; align-items: center; gap: 8px; flex-wrap: nowrap; white-space: nowrap; margin-left: auto; }
      @media (max-width: 576px) {
        .inspection-actions-left .ant-btn, .inspection-actions-right .ant-btn { min-width: auto !important; padding: 0 10px; height: 32px; font-size: 14px; }
      }
      @media (max-width: 380px) {
        .inspection-actions { gap: 6px; }
        .inspection-actions-left .ant-btn, .inspection-actions-right .ant-btn { padding: 0 8px; height: 30px; font-size: 12px; }
      }
    `;
  }, []);

  // Handle company pre-selection when companies or companyId changes
  useEffect(() => {
    if (propCompanyId && companies.length > 0) {
      // Find the company by ID from props
      const company = companies.find(c => c.id === propCompanyId || c.id === String(propCompanyId));
      if (company && selectedCompany !== company.id) {
        selectedCompanyRef.current = company.id;
        selectedCompanyNameRef.current = company.name || null;
        setSelectedCompany(company.id);
        setSelectedCompanyName(company.name || null);
        // Trigger fleet fetch for this company
        fetchFleets(company.id);
      }
    }
  }, [propCompanyId, companies]);

  useEffect(() => {
    if (!selectedCompany) return;
    if (selectedCompanyName) return;
    const fleetInfo = templateJson?.templates?.[0]?.FleetInformation;
    if (!Array.isArray(fleetInfo)) return;
    const companyField = fleetInfo.find(field => field?.title && field.title.toLowerCase().includes('company'));
    const nameFromTemplate = companyField?.response?.text;
    if (nameFromTemplate) {
      setSelectedCompanyName(nameFromTemplate);
    }
  }, [selectedCompany, selectedCompanyName, templateJson]);

  // Clear company highlight once a company is selected
  useEffect(() => {
    if (selectedCompany) removeInvalid('company');
  }, [selectedCompany, removeInvalid]);

  // Clear vehicle highlight once a vehicle is selected
  useEffect(() => {
    if (selectedVehicle?.registration) removeInvalid('vehicle');
  }, [selectedVehicle, removeInvalid]);

  // Initialize selected vehicle from template only when none is selected
  useEffect(() => {
    if (!templateJson?.templates?.[0]) return;
    if (selectedVehicle) return; // don't override manual edits/selection

    const template = templateJson.templates[0];

    if (template.FleetInformation) {
      const fleetInfo = template.FleetInformation;
      const regField = fleetInfo.find(field => {
        const title = field.title?.toLowerCase() || '';
        return title.includes('registration') || title.includes('reg no') || title.includes('regno') || title.includes('rego') || title.includes('license plate') || title.includes('number plate');
      });
      if (regField?.response?.fleetInfo) {
        setSelectedVehicle(regField.response.fleetInfo);
        setSearchTerm(regField.response.fleetInfo.registration || '');
        setInputValue(regField.response.fleetInfo.registration || '');
      } else if (regField?.response?.text) {
        setSearchTerm(regField.response.text);
        setInputValue(regField.response.text);
      }
    }
  }, [templateJson, companies, selectedVehicle]);

  useEffect(() => {
    // Only apply serverImages if we haven't already loaded from a local draft
    // Local draft may have newer image state that we don't want to overwrite
    if (serverImages?.length > 0 && !localDraftLoadedRef.current) {
      const serverFiles = imagesArrayToUploadMap(serverImages);
      setServerFileMap(serverFiles);
    }
  }, [serverImages]);

  // Fetch companies on component mount
  useEffect(() => {
    fetchCompanies();
  }, []);

  // NOTE: Auto-select when only one vehicle result was removed intentionally.
  // Users should always pick manually from the dropdown.

  // Helper to normalize raw vehicle data from the paginated fleet response
  const normalizeVehicleData = useCallback((response) => {
    let vehiclesData = [];
    let totalCount = 0;

    // Handle error envelopes (e.g. { success: false, errorCode: 'COMPANYID_NOT_FOUND', ... })
    if (response && response.success === false) {
      return { normalized: [], totalCount: 0 };
    }

    if (response && !Array.isArray(response) && Array.isArray(response.items)) {
      vehiclesData = response.items;
      totalCount = response.totalCount ?? vehiclesData.length;
    } else if (Array.isArray(response)) {
      vehiclesData = response;
      totalCount = vehiclesData.length;
    } else if (response?.data && Array.isArray(response.data)) {
      vehiclesData = response.data;
      totalCount = vehiclesData.length; f
    }
    console.log('vehiclesData', vehiclesData);

    const normalized = vehiclesData.map(vehicle => {
      const vm = vehicle.vehicleModel || {};
      console.log('vehicle', vehicle);
      return {
        ...vehicle,
        // Flatten vehicleModel fields to top level for handleVehicleSelect compatibility
        id: vehicle.id || vm.id || '',
        registration: vehicle.registration || vehicle.registrationNumber || vm.registration || vm.registrationNumber || '',
        reg: vehicle.registration || vehicle.registrationNumber || vm.registration || vm.registrationNumber || '',
        fleet: vehicle.fleet || vehicle.fleetNumber || vm.fleet || vm.fleetNumber || '',
        make: vehicle.make || vm.make || '',
        model: vehicle.model || vm.model || '',
        year: vehicle.year || vehicle.manufactureYear || vm.year || vm.manufactureYear || '',
        vin: vehicle.vin || vm.vin || '',
        currentHours: vehicle.currentHours ?? vm.currentHours ?? 0,
        currentKm: vehicle.currentKm ?? vm.currentKm ?? 0,
        odometer: vehicle.odometer ?? vm.odometer ?? vehicle.currentKm ?? vm.currentKm ?? 0,
        _original: vehicle,
      };
    });

    return { normalized, totalCount };
  }, []);

  // Fetch vehicles when search term changes – uses paginated getCompanyFleets with server-side search
  useEffect(() => {
    if (!searchTerm || !selectedCompany) return;
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        setIsManualVehicleSelect(false);
        setVehicleLoading(true);
        const response = await getCompanyFleets(selectedCompany, {
          pageSize: VEHICLE_PAGE_SIZE,
          pageIndex: 0,
          searchTerm,
        });

        const { normalized, totalCount } = normalizeVehicleData(response);
        if (!cancelled) {
          setVehicles(normalized);
          setVehiclePageIndex(0);
          setVehicleHasMore(normalized.length < totalCount);
        }
      } catch (error) {
        if (!cancelled) {
          // Treat 404 / not-found as empty results (no vehicles for this company)
          const status = error?.response?.status;
          const errorCode = error?.response?.data?.errorCode;
          if (status === 404 || errorCode === 'COMPANYID_NOT_FOUND') {
            setVehicles([]);
            setVehiclePageIndex(0);
            setVehicleHasMore(false);
          } else {
            console.error('Failed to fetch vehicles:', error);
            message.error('Failed to fetch vehicles');
          }
        }
      } finally {
        if (!cancelled) setVehicleLoading(false);
      }
    }, 700); // debounce
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [searchTerm, selectedCompany, normalizeVehicleData]);

  // Debounced vehicle search – uses paginated getCompanyFleets with server-side search
  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      if (!searchValue || !selectedCompany) return;
      setVehicleLoading(true);

      getCompanyFleets(selectedCompany, {
        pageSize: VEHICLE_PAGE_SIZE,
        pageIndex: 0,
        searchTerm: searchValue,
      })
        .then(response => {
          const { normalized, totalCount } = normalizeVehicleData(response);
          setVehicles(normalized);
          setVehiclePageIndex(0);
          setVehicleHasMore(normalized.length < totalCount);
        })
        .catch(error => {
          const status = error?.response?.status;
          const errorCode = error?.response?.data?.errorCode;
          if (status === 404 || errorCode === 'COMPANYID_NOT_FOUND') {
            setVehicles([]);
            setVehiclePageIndex(0);
            setVehicleHasMore(false);
          } else {
            console.error('Error searching vehicles:', error);
            message.error('Failed to search vehicles');
          }
        })
        .finally(() => setVehicleLoading(false));
    }, 700),
    [selectedCompany, normalizeVehicleData]
  );

  // Load more vehicles (next page) for infinite scroll in the AutoComplete dropdown
  const loadMoreVehicles = useCallback(() => {
    if (!selectedCompany || vehicleLoading || !vehicleHasMore || !searchTerm) return;
    const nextPage = vehiclePageIndex + 1;
    setVehicleLoading(true);

    getCompanyFleets(selectedCompany, {
      pageSize: VEHICLE_PAGE_SIZE,
      pageIndex: nextPage,
      searchTerm,
    })
      .then(response => {
        const { normalized, totalCount } = normalizeVehicleData(response);
        setVehicles(prev => {
          const combined = [...prev];
          normalized.forEach(v => {
            const key = v.registration || v.vin;
            if (key && !combined.some(existing => (existing.registration || existing.vin) === key)) {
              combined.push(v);
            }
          });
          return combined;
        });
        setVehiclePageIndex(nextPage);
        setVehicleHasMore((vehicles.length + normalized.length) < totalCount);
      })
      .catch(error => {
        const status = error?.response?.status;
        const errorCode = error?.response?.data?.errorCode;
        if (status === 404 || errorCode === 'COMPANYID_NOT_FOUND') {
          setVehicleHasMore(false);
        } else {
          console.error('Error loading more vehicles:', error);
        }
      })
      .finally(() => setVehicleLoading(false));
  }, [selectedCompany, vehicleLoading, vehicleHasMore, searchTerm, vehiclePageIndex, vehicles.length, normalizeVehicleData]);

  // Deduplicate vehicles (server-side search already filters by searchTerm)
  const filteredVehicles = useMemo(() => {
    if (!searchTerm) return [];

    const uniqueVehicles = new Map();
    vehicles.forEach(vehicle => {
      if (!vehicle) return;
      const key = vehicle.registration || vehicle.vin || null;
      if (!key) return;
      if (!uniqueVehicles.has(key)) {
        uniqueVehicles.set(key, vehicle);
      }
    });

    return Array.from(uniqueVehicles.values());
  }, [vehicles, searchTerm]);

  const clearVehicleFields = () => {
    // Reset state
    setSelectedVehicle(null);
    setSearchTerm('');
    setInputValue('');
    setSelectedFleetId(null);
    setVehicles([]);
    setVehiclePageIndex(0);
    setVehicleHasMore(false);

    // Batch-clear vehicle-related fields in the current template
    const prev = templateRef.current;
    if (prev?.templates?.[0]?.FleetInformation) {
      const next = deepClone(prev);
      const patterns = ['registration', 'truck', 'vin', 'make', 'model', 'year', 'hours', 'odometer', 'mileage', 'kilometers', 'km', 'fleet', 'fleet number'];
      next.templates[0].FleetInformation = next.templates[0].FleetInformation.map((field) => {
        const title = field.title?.toLowerCase() || '';
        const isVehicleField = patterns.some((p) => title.includes(p));
        if (!isVehicleField) return field;
        const resp = { ...(field.response || {}) };
        resp.text = '';
        resp.optionId = '';
        if (title.includes('registration') || title.includes('truck')) {
          resp.fleetInfo = null;
        }
        return { ...field, response: resp };
      });
      templateRef.current = next;
      setTemplateJson(next);
    }
  };

  const handleVehicleSelect = (vehicle) => {
    // If no vehicle is selected, clear all fields
    if (!vehicle) {
      clearVehicleFields();
      return;
    }

    // Create a new vehicle object with all properties to ensure consistency
    const vehicleWithDefaults = {
      id: vehicle.id || '',
      registration: vehicle.registration || vehicle.registration || '',
      fleet: vehicle.fleet || vehicle._original?.fleet || '',
      make: vehicle.make || vehicle._original?.make || '',
      model: vehicle.model || vehicle._original?.model || '',
      year: vehicle.year || vehicle._original?.year || '',
      vin: vehicle.vin || vehicle._original?.vin || '',
      currentHours: vehicle.currentHours || 0,
      currentKm: vehicle.odometer || vehicle.currentKm || 0,
      _original: vehicle._original || vehicle // Preserve original data
    };

    // Set the selected vehicle in state with all properties
    setSelectedVehicle(vehicleWithDefaults);
    setInputValue(vehicleWithDefaults.registration || '');
    setSearchTerm(vehicleWithDefaults.registration || '');

    // Create truck info object with all vehicle details
    const fleetInfo = {
      ...vehicleWithDefaults,
      // Ensure all fields have proper fallbacks
      registration: vehicleWithDefaults.registration,
      fleet: vehicleWithDefaults.fleet,
      make: vehicleWithDefaults.make,
      model: vehicleWithDefaults.model,
      year: vehicleWithDefaults.year,
      vin: vehicleWithDefaults.vin,
      currentHours: vehicleWithDefaults.currentHours,
      currentKm: vehicleWithDefaults.currentKm
    };

    // Update the template JSON with vehicle information
    if (templateJson?.templates?.[0]?.FleetInformation) {
      const fleetFields = templateJson.templates[0].FleetInformation;

      const existingFleetFields = (templateRef.current?.templates?.[0]?.FleetInformation || templateJson.templates[0].FleetInformation || []);
      const getExistingText = (id) => {
        const found = existingFleetFields.find((f) => String(f?.itemId) === String(id));
        const txt = found?.response?.text;
        return txt === undefined || txt === null ? '' : String(txt);
      };

      // Define all possible field mappings with their update logic
      const fieldMappings = [
        {
          patterns: ['registration', 'registration number', 'reg no', 'regno', 'rego', 'license plate', 'number plate'],
          value: vehicle.registration || '',
          updateField: (field) => {
            updateItemResponse(field.itemId, {
              text: vehicle.registration || '',
              optionId: vehicle.registration || '',
              fleetInfo: {
                ...fleetInfo,
                registration: vehicle.registration || ''
              }
            });
          }
        },
        {
          patterns: ['fleet', 'fleet number'],
          value: vehicle.fleet || '',
          updateField: (field) => {
            updateItemResponse(field.itemId, {
              text: vehicle.fleet || '',
              optionId: vehicle.fleet || '',
              fleetInfo: {
                ...fleetInfo,
                id: vehicle.id || '',
                name: vehicle.fleet || '',
                fleet: vehicle.fleet || ''
              }
            });
          }
        },
        {
          patterns: ['make', 'manufacturer', 'brand'],
          value: vehicle.make || '',
          skipIfHasText: true,
          updateField: (field) => {
            updateItemResponse(field.itemId, {
              text: vehicle.make || '',
              optionId: vehicle.make || '',
            });
          }
        },
        {
          patterns: ['model'],
          value: vehicle.model || '',
          skipIfHasText: true,
          updateField: (field) => {
            updateItemResponse(field.itemId, {
              text: vehicle.model || '',
              optionId: vehicle.model || '',
            });
          }
        },
        {
          patterns: ['year', 'yr', 'model year'],
          value: vehicle.year ? vehicle.year.toString() : '',
          skipIfHasText: true,
          updateField: (field) => {
            updateItemResponse(field.itemId, {
              text: vehicle.year ? vehicle.year.toString() : '',
              optionId: vehicle.year ? vehicle.year.toString() : '',
            });
          }
        },
        {
          patterns: ['vin', 'chassis', 'vehicle identification'],
          value: vehicle.vin || '',
          skipIfHasText: true,
          updateField: (field) => {
            updateItemResponse(field.itemId, {
              text: vehicle.vin || '',
              optionId: vehicle.vin || '',
            });
          }
        },
        {
          patterns: ['hours', 'engine hours'],
          value: vehicle.currentHours ? vehicle.currentHours.toString() : '0',
          skipIfHasText: true,
          updateField: (field) => {
            updateItemResponse(field.itemId, {
              text: vehicle.currentHours ? vehicle.currentHours.toString() : '0',
              optionId: vehicle.currentHours ? vehicle.currentHours.toString() : '0',
            });
          }
        },
        {
          patterns: ['odometer', 'odometer reading', 'km', 'kilometers', 'mileage'],
          value: (vehicle.odometer || vehicle.currentKm || 0).toString(),
          skipIfHasText: true,
          updateField: (field) => {
            updateItemResponse(field.itemId, {
              text: (vehicle.odometer || vehicle.currentKm || 0).toString(),
              optionId: (vehicle.odometer || vehicle.currentKm || 0).toString(),
            });
          }
        }
      ];


      // Process each field in the fleet information
      fleetFields.forEach(field => {
        if (!field?.itemId) return;

        const fieldName = field.title?.toLowerCase() || '';

        // Find the first matching field mapping
        const mapping = fieldMappings.find(mapping =>
          mapping.patterns.some(pattern => fieldName.toLowerCase().includes(pattern))
        );

        // If we found a matching field, update it using the custom update function
        if (mapping && mapping.updateField) {
          if (mapping.skipIfHasText) {
            const existingText = getExistingText(field.itemId).trim();
            if (existingText.length > 0) {
              return;
            }
          }
          mapping.updateField(field);
        }
      });
    }

    // Force a re-render to ensure UI updates
    setUpdateKey(prev => prev + 1);

    // Update fleet selection if needed
    if (vehicle.fleet) {
      const fleetField = templateJson?.templates?.[0]?.FleetInformation?.find(item =>
        item.title && (item.title.toLowerCase().includes('fleet') || item.title.toLowerCase().includes('fleet number'))
      );

      if (fleetField?.id) {
        const fleetValue = vehicle.fleet;

        // Try to find a matching fleet in the fleets array
        const matchingFleet = fleets?.find(f =>
          f.name === fleetValue ||
          f.id === fleetValue ||
          f.name?.toLowerCase() === fleetValue?.toLowerCase() ||
          f.id?.toString() === fleetValue?.toString()
        );

        const fleetDisplayText = matchingFleet
          ? (matchingFleet.name || matchingFleet.id || 'Not Available')
          : (fleetValue === 'Not Available' ? 'Not Available' : fleetValue);

        const fleetOptionId = matchingFleet
          ? (matchingFleet.id || 'N/A')
          : (fleetValue === 'Not Available' ? 'N/A' : fleetValue);

        // Create a complete fleet info object
        const fleetInfo = {
          id: matchingFleet?.id || fleetValue,
          name: matchingFleet?.name || fleetValue,
          registration: vehicle.registration,
          make: vehicle.make,
          model: vehicle.model,
          year: vehicle.year
        };

        // Update the fleet field with all vehicle info
        updateItemResponse(fleetField.id, {
          text: fleetDisplayText,
          optionId: fleetOptionId,
          fleetInfo: fleetInfo
        });

        // Update selected fleet ID if we found a match
        if (matchingFleet) {
          setSelectedFleetId(matchingFleet.id);
        } else {
          setSelectedFleetId(null);
        }
      }
    }
  };

  const findSavedCompanyIdFromTemplate = (companies) => {
    const fleetInfo = templateRef.current?.templates?.[0]?.FleetInformation;
    if (!Array.isArray(fleetInfo)) return null;

    const companyField = fleetInfo.find(f =>
      typeof f?.title === 'string' && f.title.toLowerCase().includes('company')
    );

    const savedText = companyField?.response?.text;
    const savedId = companyField?.response?.companyId;

    if (!savedText && !savedId) return null;

    const match = companies.find(c =>
      (savedId && c.id === String(savedId)) ||
      (savedText && c.name === savedText)
    );

    return match?.id ?? null;
  };

  const resolveInitialCompanyId = (companies) => {
    const existing = selectedCompanyRef.current || propCompanyId;
    if (existing) {
      const match = companies.find(c => c.id === String(existing));
      return match?.id ?? String(existing);
    }

    const saved = findSavedCompanyIdFromTemplate(companies);
    if (saved) return saved;

    if (companies.length === 1) return companies[0].id;

    return null;
  };

  const normalizeCompanies = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw
      .map(c => ({
        id: String(c?.id ?? ''),
        name: c?.name?.trim() || 'Unnamed Company',
      }))
      .filter(c => c.id);
  };

  const fetchCompanies = async () => {
    if (typeof templateId !== 'string' || templateId.trim() === '') {
      console.error('Invalid templateId:', templateId);
      message.error('Error: Invalid template ID format.');
      return;
    }

    try {
      setLoadingCompanies(true);

      const raw = await getInspectionTemplateCompanies(templateId);
      const companies = normalizeCompanies(raw);
      setCompanies(companies);

      const companyId = resolveInitialCompanyId(companies);

      // Only update selection if we actually resolved something
      if (companyId) {
        setSelectedCompany(companyId);
        const matched = companies.find(c => c.id === companyId);
        if (matched?.name) setSelectedCompanyName(matched.name);
        fetchFleets(companyId);
        return;
      }

      // If nothing resolved and nothing forced it, clear selection
      if (!selectedCompanyRef.current && !propCompanyId) {
        setSelectedCompany(null);
        setSelectedCompanyName(null);
      }

      // Reset dependent selections
      setSelectedFleetId(null);
      setFleets([]);
      setTrucks([]);
    } catch (error) {
      console.error('Error fetching companies:', error);
      message.error('Failed to load companies.');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const selectedId = selectedCompany ? String(selectedCompany) : null;
  const hasSelectedInList = selectedId && companies.some(c => c.id === selectedId);

  const companyOptions = hasSelectedInList
    ? companies
    : selectedId
      ? [{ id: selectedId, name: selectedCompanyName || `Company ${selectedId}` }, ...companies]
      : companies;

  const options = companyOptions.map(c => ({ value: c.id, label: c.name }));

  // Fetch fleets when company is selected (supports pagination + search)
  const fetchFleets = async (companyId, { page = 0, search = '', append = false } = {}) => {
    try {
      if (!companyId) {
        console.error('No companyId provided to fetchFleets, clearing fleets');
        setFleets([]);
        setTrucks([]);
        setSelectedFleetId(null);
        return;
      }

      const normalizedCompanyId = String(companyId).trim();

      if (!normalizedCompanyId) {
        console.error('Empty companyId after normalization in fetchFleets');
        return;
      }

      setLoadingFleets(true);

      const response = await getCompanyFleets(normalizedCompanyId, {
        pageSize: FLEET_PAGE_SIZE,
        pageIndex: page,
        searchTerm: search || undefined,
      });

      // Handle paginated envelope: { items, totalCount, pageIndex, pageSize }
      let rawItems = [];
      let totalCount = 0;

      if (response && !Array.isArray(response) && Array.isArray(response.items)) {
        rawItems = response.items;
        totalCount = response.totalCount ?? rawItems.length;
      } else if (Array.isArray(response)) {
        // Backward-compatible flat array (server didn't page)
        rawItems = response;
        totalCount = rawItems.length;
      } else {
        console.error('Unexpected response shape from getCompanyFleets:', response);
        if (!append) setFleets([]);
        return;
      }

      // Process the fleet data
      const processedFleets = rawItems
        .map(item => {
          const fleet = item.fleet || (item.vehicleModel?.fleet) || '';
          if (!fleet) return null;
          return {
            id: fleet,
            name: fleet,
            _original: item
          };
        })
        .filter(Boolean)
        .reduce((acc, current) => {
          if (!acc.some(f => f.id === current.id)) {
            acc.push(current);
          }
          return acc;
        }, []);

      // Update pagination state
      const loadedSoFar = append ? fleets.length + processedFleets.length : processedFleets.length;
      setFleetPageIndex(page);
      setFleetTotalCount(totalCount);
      setFleetHasMore(loadedSoFar < totalCount);

      // Append or replace
      if (append) {
        setFleets(prev => {
          const combined = [...prev];
          processedFleets.forEach(f => {
            if (!combined.some(existing => existing.id === f.id)) {
              combined.push(f);
            }
          });
          return combined;
        });
      } else {
        setFleets(processedFleets);
        setSelectedFleetId(null);
        setTrucks([]);
      }
    } catch (error) {
      console.error('Error fetching fleets:', error);
      message.error('Failed to load fleets. Please try again.');
      if (!append) {
        setFleets([]);
        setTrucks([]);
      }
    } finally {
      setLoadingFleets(false);
      setLoadingTrucks(false);
    }
  };

  // Load more fleets (next page) for infinite scroll
  const loadMoreFleets = useCallback(() => {
    if (!selectedCompany || loadingFleets || !fleetHasMore) return;
    fetchFleets(selectedCompany, {
      page: fleetPageIndex + 1,
      search: fleetSearchTerm,
      append: true,
    });
  }, [selectedCompany, loadingFleets, fleetHasMore, fleetPageIndex, fleetSearchTerm]);

  // Debounced fleet search handler
  const debouncedFleetSearch = useCallback(
    debounce((companyId, term) => {
      if (!companyId) return;
      setFleetPageIndex(0);
      setFleetHasMore(false);
      fetchFleets(companyId, { page: 0, search: term, append: false });
    }, 400),
    []
  );

  // Handle fleet search input change
  const handleFleetSearch = useCallback((value) => {
    setFleetSearchTerm(value);
    if (selectedCompany) {
      debouncedFleetSearch(selectedCompany, value);
    }
  }, [selectedCompany, debouncedFleetSearch]);

  // Fetch trucks when fleet is selected
  const fetchTrucks = async (fleetId) => {
    if (!fleetId) {
      setTrucks([]);
      setLoadingTrucks(false);
      return;
    }

    try {
      setLoadingTrucks(true);

      // Call the API to search for vehicles with the current search term
      const response = await getAllVehicles(searchTerm || '');

      // Handle different response structures
      let trucksArray = [];

      if (Array.isArray(response)) {
        // If response is already an array, use it directly
        trucksArray = response;
      } else if (response && Array.isArray(response.data)) {
        // If response has a data property that's an array
        trucksArray = response.data;
      } else if (response && typeof response === 'object') {
        // If response is a single truck object or has a different structure
        trucksArray = [response];
      }

      // First, create a map to store unique registrations
      const uniqueTrucksMap = new Map();

      // Process all trucks and keep only the first occurrence of each registration
      trucksArray.forEach((truck, index) => {
        console.group(`Truck #${index + 1}`);

        // Try to find the registration number in various possible fields
        const registration = [
          truck.registration,
          truck.registrationNumber,
          truck.registrationNumber,
          truck.registration,
          truck.vehicleRegistration,
          truck.vehicle?.registration,
          truck.vehicle?.registrationNumber,
          truck.vehicle?.registrationNumber,
          truck.vehicleModel?.registration,
          truck.vehicleModel?.registration
        ].find(Boolean) || '';

        // Use registration as key; if missing, fall back to id or index to keep uniqueness
        const uniqueKey = registration || truck.id || `no-reg-${index}`;
        // Only add to the map if this key hasn't been seen before
        if (!uniqueTrucksMap.has(uniqueKey)) {
          // Extract make and model from various possible locations
          const make = [
            truck.make,
            truck.manufacturer,
            truck.vehicle?.make,
            truck.vehicle?.manufacturer,
            truck.vehicleModel?.make,
            truck.vehicleModel?.manufacturer
          ].find(Boolean) || '';

          const model = [
            truck.model,
            truck.modelName,
            truck.vehicle?.model,
            truck.vehicle?.modelName,
            truck.vehicleModel?.model,
            truck.vehicleModel?.modelName
          ].find(Boolean) || '';

          const mappedTruck = {
            id: truck.id || truck.vehicleModel?.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
            reg: registration,
            make: make,
            model: model,
            year: truck.year || truck.manufactureYear || truck.Year || '',
            currentKm: truck.kms || truck.km || truck.currentKm || truck.mileage || truck.odometer || 0,
            currentHours: truck.hours || truck.hrs || truck.currentHours || truck.engineHours || 0,
            _original: truck  // Keep original data for reference
          };

          uniqueTrucksMap.set(uniqueKey, mappedTruck);
          console.groupEnd();
        }
      });

      // Convert the map values back to an array
      const mappedTrucks = Array.from(uniqueTrucksMap.values());

      setTrucks(mappedTrucks);

      // If there's only one truck, automatically select it
      if (mappedTrucks.length === 1) {
        debugger;
        const singleTruck = mappedTrucks[0];

        // Use a timeout to ensure the component is ready for updates
        setTimeout(() => {
          try {
            // Set the selected truck in state
            setSelectedTruck(singleTruck);
            setInputValue(singleTruck.registration || '');
            setSearchTerm(singleTruck || '');

            // Create truck info object with all vehicle details
            const fleetInfo = {
              id: singleTruck.id,
              registration: singleTruck.registration || '',
              fleet: singleTruck._original?.fleet || singleTruck._original?.fleetNumber || '',
              make: singleTruck.make || singleTruck._original?.make || '',
              model: singleTruck.model || singleTruck._original?.model || '',
              year: singleTruck.year || singleTruck._original?.year || '',
              vin: singleTruck._original?.vin || '',
              currentHours: singleTruck.currentHours || 0,
              currentKm: singleTruck.currentKm || 0
            };

            // Update all relevant fields in the form
            const fleetInfoFields = templateJson?.templates?.[0]?.FleetInformation || [];

            // Define all possible field mappings with their update logic
            const fieldMappings = [
              {
                patterns: ['registration', 'registration number', 'reg no', 'regno', 'rego', 'license plate', 'number plate'],
                value: singleTruck.registration || '',
                updateField: (field) => {
                  updateItemResponse(field.itemId, {
                    text: singleTruck.registration || '',
                    optionId: singleTruck.registration || '',
                    fleetInfo: {
                      ...fleetInfo,
                      registration: singleTruck.registration || ''
                    }
                  });
                }
              },
              {
                patterns: ['fleet', 'fleet number'],
                value: fleetInfo.fleet || '',
                updateField: (field) => {
                  updateItemResponse(field.itemId, {
                    text: fleetInfo.fleet || '',
                    optionId: fleetInfo.fleet || '',
                    fleetInfo: {
                      ...fleetInfo,
                      id: singleTruck.id || '',
                      name: fleetInfo.fleet || ''
                    }
                  });
                }
              },
              {
                patterns: ['make', 'manufacturer', 'brand'],
                value: fleetInfo.make || '',
                updateField: (field) => {
                  updateItemResponse(field.itemId, {
                    text: fleetInfo.make || '',
                    optionId: fleetInfo.make || '',
                    fleetInfo: fleetInfo
                  });
                }
              },
              {
                patterns: ['model'],
                value: fleetInfo.model || '',
                updateField: (field) => {
                  updateItemResponse(field.itemId, {
                    text: fleetInfo.model || '',
                    optionId: fleetInfo.model || '',
                    fleetInfo: fleetInfo
                  });
                }
              },
              {
                patterns: ['year'],
                value: fleetInfo.year ? fleetInfo.year.toString() : '',
                updateField: (field) => {
                  updateItemResponse(field.itemId, {
                    text: fleetInfo.year ? fleetInfo.year.toString() : '',
                    optionId: fleetInfo.year ? fleetInfo.year.toString() : '',
                    fleetInfo: fleetInfo
                  });
                }
              },
              {
                patterns: ['vin'],
                value: fleetInfo.vin || '',
                updateField: (field) => {
                  updateItemResponse(field.itemId, {
                    text: fleetInfo.vin || '',
                    optionId: fleetInfo.vin || '',
                    fleetInfo: fleetInfo
                  });
                }
              },
              {
                patterns: ['hours', 'current hours'],
                value: fleetInfo.currentHours ? fleetInfo.currentHours.toString() : '0',
                updateField: (field) => {
                  updateItemResponse(field.itemId, {
                    text: fleetInfo.currentHours ? fleetInfo.currentHours.toString() : '0',
                    optionId: fleetInfo.currentHours ? fleetInfo.currentHours.toString() : '0',
                    fleetInfo: fleetInfo
                  });
                }
              },
              {
                patterns: ['odometer', 'km', 'current km'],
                value: fleetInfo.currentKm ? fleetInfo.currentKm.toString() : '0',
                updateField: (field) => {
                  updateItemResponse(field.itemId, {
                    text: fleetInfo.currentKm ? fleetInfo.currentKm.toString() : '0',
                    optionId: fleetInfo.currentKm ? fleetInfo.currentKm.toString() : '0',
                    fleetInfo: fleetInfo
                  });
                }
              }
            ];

            // Process each field in the fleet information
            fleetInfoFields.forEach(field => {
              if (!field?.itemId) return;

              const fieldName = field.title?.toLowerCase() || '';

              // Find the first matching field mapping
              const mapping = fieldMappings.find(mapping =>
                mapping.patterns.some(pattern => fieldName.includes(pattern))
              );

              // If we found a matching field, update it using the custom update function
              if (mapping) {
                mapping.updateField(field);
              }
            });

            // Find the registration field in the template
            const truckField = fleetInfoFields.find(
              (field) => {
                const t = field.title?.toLowerCase() || '';
                return t.includes('registration') || t.includes('reg no') || t.includes('regno') || t.includes('rego') || t.includes('license plate') || t.includes('number plate');
              }
            );

            if (truckField) {
              // Create a complete truck info object with all available details
              const fleetInfo = {
                ...singleTruck,
                registration: singleTruck.registration || '',
                make: singleTruck.make || '',
                model: singleTruck.model || '',
                year: singleTruck.year || singleTruck._original?.year || '',
                currentKm: singleTruck.currentKm || 0,
                currentHours: singleTruck.currentHours || 0
              };

              // Create a map of all field updates to apply
              const fieldUpdates = {};
              const responseUpdates = [];

              // Update all relevant form fields
              fleetInfoFields.forEach(field => {
                if (!field?.itemId) return;

                const fieldTitle = field.title?.toLowerCase() || '';
                let fieldValue = '';
                let responseValue = {};

                if (field.itemId === truckField.itemId || fieldTitle.includes('registration')) {
                  // Handle truck registration field
                  fieldValue = singleTruck.registration;
                  responseValue = {
                    text: singleTruck.registration,
                    optionId: singleTruck.registration,
                    fleetInfo: fleetInfo
                  };
                } else if (fieldTitle.includes('make') && fleetInfo.make) {
                  fieldValue = fleetInfo.make;
                  responseValue = { text: fleetInfo.make };
                } else if (fieldTitle.includes('model')) {
                  // Always set model explicitly; if missing, clear the field
                  fieldValue = fleetInfo.model || '';
                  responseValue = { text: fleetInfo.model || '' };
                } else if (fieldTitle.includes('year') && fleetInfo.year) {
                  fieldValue = String(fleetInfo.year);
                  responseValue = { text: String(fleetInfo.year) };
                } else if ((fieldTitle.includes('mileage') || fieldTitle.includes('km') || fieldTitle.includes('odometer')) && fleetInfo.currentKm) {
                  fieldValue = String(fleetInfo.currentKm);
                  responseValue = { text: String(fleetInfo.currentKm) };
                } else if (fieldTitle.includes('hours') && fleetInfo.currentHours) {
                  fieldValue = String(fleetInfo.currentHours);
                  responseValue = { text: String(fleetInfo.currentHours) };
                }

                if (fieldTitle.includes('model')) {
                  // Push update even when empty to clear any previous value
                  fieldUpdates[field.itemId] = fieldValue;
                  responseUpdates.push({ itemId: field.itemId, response: responseValue });
                } else if (fieldValue) {
                  fieldUpdates[field.itemId] = fieldValue;
                  responseUpdates.push({ itemId: field.itemId, response: responseValue });
                }
              });

              if (Object.keys(fieldUpdates).length > 0) {
                try {
                  // Update form fields first
                  form.setFieldsValue(fieldUpdates);

                  // Then update the responses for each field
                  responseUpdates.forEach(({ itemId, response }) => {
                    updateItemResponse(itemId, response);
                  });

                  // Update the selected truck state
                  setSelectedTruck(singleTruck);
                  // Also set selectedVehicle so dynamic fields can auto-populate from it
                  setSelectedVehicle({
                    id: fleetInfo.id || singleTruck.id || '',
                    registration: fleetInfo.registration || singleTruck.registration || singleTruck.reg || '',
                    fleet: fleetInfo.fleet || singleTruck.fleet || '',
                    make: fleetInfo.make || singleTruck.make || '',
                    model: fleetInfo.model || singleTruck.model || '',
                    year: fleetInfo.year || singleTruck.year || '',
                    vin: fleetInfo.vin || singleTruck._original?.vin || '',
                    currentHours: fleetInfo.currentHours || singleTruck.currentHours || 0,
                    currentKm: fleetInfo.currentKm || singleTruck.currentKm || 0,
                    _original: singleTruck._original || singleTruck
                  });

                  // Update fleet number if available
                  if (singleTruck._original?.vehicleModel?.fleet) {
                    const fleetField = fleetInfoFields.find(field =>
                      field.title?.toLowerCase().includes('fleet')
                    );

                    if (fleetField?.itemId) {
                      updateItemResponse(fleetField.itemId, {
                        text: singleTruck._original.vehicleModel.fleet
                      });
                    }
                  }

                  // Force a re-render after a short delay
                  setTimeout(() => {
                    setUpdateKey(prev => prev + 1);

                  }, 50);
                } catch (error) {
                  console.error('Error updating form fields:', error);
                }
              }
            }
          } catch (error) {
            console.error('Error during auto-selection:', error);
          }
        }, 0);
      }
    } catch (error) {
      console.error('Error fetching trucks:', error);
      message.error('Failed to load trucks. Please try again.');
      setTrucks([]);
    } finally {
      setLoadingTrucks(false);
    }
  };

  const resetForm = () => {
    setSelectedFleetId(null);
    setTrucks([]);
    setFleets([]);
    setFleetSearchTerm('');
    setFleetPageIndex(0);
    setFleetHasMore(false);
    setFleetTotalCount(0);
    setInvalidFields([]);

    // Reset truck info in the response
    const fleetInfoFields = templateJson?.templates?.[0]?.FleetInformation || [];
    const fieldUpdates = {};

    fleetInfoFields.forEach(field => {
      if (!field?.itemId) return;

      // Reset the field value
      fieldUpdates[field.itemId] = '';

      // Update the response for the field
      updateItemResponse(field.itemId, {
        text: '',
        fleetInfo: null
      });
    });

    // Update all form fields at once
    form.setFieldsValue(fieldUpdates);

    // Force a re-render to ensure all fields are updated
    setUpdateKey(prev => prev + 1);
  };

  const handleCompanyChange = async (companyId) => {
    // Only proceed if the company has actually changed
    if (companyId === selectedCompany) {
      return;
    }

    // Ensure companyId is a string and not empty
    const normalizedCompanyId = String(companyId || '').trim();

    if (!normalizedCompanyId) {
      console.error('Empty companyId provided to handleCompanyChange');
      return;
    }

    // Clear all vehicle-related state
    try {
      debouncedSaveDraft.cancel?.();
    } catch { }
    setVehicles([]);
    setSelectedVehicle(null);
    setSearchTerm('');
    setInputValue('');
    setTrucks([]);
    setSelectedTruck(null);

    // Clear fleet-related state
    setFleets([]);
    setSelectedFleetId(null);
    setFleetSearchTerm('');
    setFleetPageIndex(0);
    setFleetHasMore(false);
    setFleetTotalCount(0);

    // Reset the form fields
    resetForm();
    setInvalidFields([]);

    try {
      // Update the company first
      setSelectedCompany(normalizedCompanyId);
      const company = companies.find(c => c.id === normalizedCompanyId || c.id === String(normalizedCompanyId));
      const companyName = company?.name || null;
      setSelectedCompanyName(companyName);
      selectedCompanyRef.current = normalizedCompanyId;
      selectedCompanyNameRef.current = companyName;

      // Persist company selection into template JSON (so it becomes part of the saved draft content)
      const fleetInfoFields = templateRef.current?.templates?.[0]?.FleetInformation || templateJson?.templates?.[0]?.FleetInformation || [];
      if (Array.isArray(fleetInfoFields)) {
        const companyField = fleetInfoFields.find(field => field?.title && field.title.toLowerCase().includes('company'));
        if (companyField?.itemId) {
          updateItemResponse(companyField.itemId, {
            text: companyName || '',
            optionId: normalizedCompanyId,
            companyId: normalizedCompanyId,
          });
        }
      }

      // If offline, save immediately (so leaving/re-entering preserves company)
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const snapshot = buildDraftSnapshotRef.current();
        if (snapshot) {
          snapshot.companyId = normalizedCompanyId;
          snapshot.companyName = companyName || '';
          snapshot.data = {
            ...(snapshot.data || {}),
            selectedCompany: normalizedCompanyId,
            companyName: companyName || null,
          };
          try {
            await saveInspectionDraft(snapshot);
          } catch (e) {
            console.error('Failed to save local inspection draft after company change:', e);
          }
        }
      }

      // Fetch fleets for the new company
      await fetchFleets(normalizedCompanyId);

      // When company changes, reset vehicle search
      setSearchTerm('');
      setVehicles([]);

    } catch (error) {
      console.error('Error in handleCompanyChange:', error);
      message.error('Failed to load company data');
    }
  };

  // State for selected truck and loading state
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [loadingTrucks, setLoadingTrucks] = useState(false);

  // Load inspection data when responseId changes (when opening a draft)
  useEffect(() => {
    const loadInspectionData = async () => {
      if (!responseId) return;
      if (!draftInitComplete) return;

      try {
        const detail = await getInspectionResponseDetail(responseId, true);

        if (!detail) {
          console.error('No inspection data found for response:', responseId);
          return;
        }

        // Parse the inspection items JSON
        const parsedJson = detail?.inspectionItemsJson
          ? safeParse(detail.inspectionItemsJson)
          : null;

        if (!parsedJson) {
          console.error('Failed to parse inspection items JSON');
          return;
        }

        // Process vehicle information if available.
        // IMPORTANT: if we already loaded a local draft, do NOT overwrite selectedVehicle from the server,
        // because Fleet Info inputs bind to selectedVehicle and that would revert local edits.
        if (!localDraftLoadedRef.current) {
          const fleetInfo = parsedJson.templates?.[0]?.FleetInformation?.find(
            field => field?.response?.fleetInfo
          )?.response?.fleetInfo;

          if (fleetInfo) {
            setSelectedVehicle({
              id: fleetInfo.id,
              registration: fleetInfo.registration || '',
              fleet: fleetInfo.fleet || fleetInfo.name || '',
              make: fleetInfo.make || '',
              model: fleetInfo.model || '',
              year: fleetInfo.year || '',
              vin: fleetInfo.vin || '',
              currentHours: fleetInfo.currentHours || 0,
              currentKm: fleetInfo.currentKm || 0,
              odometer: fleetInfo.odometer || fleetInfo.currentKm || 0
            });
          }
        }

        // Apply server image counts and update state
        const baseTemplateForCounts = localDraftLoadedRef.current
          ? (templateRef.current || templateJson || parsedJson)
          : parsedJson;
        const withCounts = applyServerImageCounts(
          baseTemplateForCounts,
          Array.isArray(detail?.images) ? detail.images : []
        );

        // If local draft is loaded, keep local content and only apply image counts to it.
        // Always keep the ref in sync so snapshots and updates use the latest template.
        setTemplateJson(withCounts);
        templateRef.current = withCounts;

        // Update file maps
        if (Array.isArray(detail?.images)) {
          const nextServer = imagesArrayToUploadMap(detail.images);
          if (!localDraftLoadedRef.current) {
            const normalized = {};
            Object.entries(nextServer || {}).forEach(([k, list]) => {
              normalized[k] = dedupeUploadList(list);
            });
            setServerFileMap(normalized);
            setFilesMap({});
          } else {
            setServerFileMap((prev) => {
              const merged = { ...(prev || {}) };
              Object.entries(nextServer).forEach(([k, list]) => {
                merged[k] = dedupeUploadList([...(merged[k] || []), ...(list || [])]);
              });
              return merged;
            });
          }

          // Prefetch server images into Blobs so the draft can be used fully offline.
          // Run on initial load to ensure images are available offline.
          if (
            !serverImagesPrefetchedRef.current &&
            (typeof navigator === 'undefined' || navigator.onLine)
          ) {
            serverImagesPrefetchedRef.current = true;

            const base64DataUrlToBlob = async (dataUrl) => {
              try {
                if (!dataUrl || typeof dataUrl !== 'string') return null;
                if (!dataUrl.startsWith('data:')) return null;
                const res = await fetch(dataUrl);
                if (!res.ok) return null;
                return await res.blob();
              } catch {
                return null;
              }
            };

            const urlToBlob = async (url) => {
              try {
                if (!url || typeof url !== 'string') return null;
                if (url.startsWith('data:')) return await base64DataUrlToBlob(url);
                const res = await fetch(url);
                if (!res.ok) return null;
                return await res.blob();
              } catch {
                return null;
              }
            };

            const prefetch = async () => {
              try {
                const mergedLocal = {};
                const entries = Object.entries(nextServer || {});

                // Get current local files to avoid re-downloading images we already have as blobs
                const currentLocalFiles = filesMap || {};

                for (const [itemId, list] of entries) {
                  const serverList = Array.isArray(list) ? list : [];
                  if (serverList.length === 0) continue;

                  // Build a set of server image identifiers we already have as local blobs
                  // Match by blobPath/blobContainer or by checking if uid starts with "prefetch-"
                  const existingLocal = currentLocalFiles[itemId] || [];
                  const alreadyPrefetched = new Set();

                  existingLocal.forEach((localFile) => {
                    if (localFile?.blob || localFile?.originFileObj) {
                      // If this local file has blobPath/blobContainer, it came from a server image
                      if (localFile.blobPath) {
                        alreadyPrefetched.add(localFile.blobPath);
                      }
                      // If uid starts with "prefetch-", extract the original server uid
                      if (localFile.uid?.startsWith('prefetch-')) {
                        alreadyPrefetched.add(localFile.uid);
                      }
                    }
                  });

                  const localList = [];
                  for (let i = 0; i < serverList.length; i++) {
                    const f = serverList[i];

                    // Skip if we already have this server image as a local blob
                    const prefetchUid = `prefetch-${f?.uid || `${itemId}-${i}`}`;
                    if (alreadyPrefetched.has(f?.blobPath) || alreadyPrefetched.has(prefetchUid)) {
                      continue;
                    }

                    const blob = await urlToBlob(f?.url || f?.thumbUrl);
                    if (!blob) continue;

                    let previewUrl = '';
                    try {
                      if (typeof URL !== 'undefined' && URL.createObjectURL) {
                        previewUrl = URL.createObjectURL(blob);
                      }
                    } catch { }

                    localList.push({
                      uid: prefetchUid,
                      name: f?.name || `image-${i + 1}.jpg`,
                      status: 'done',
                      type: blob.type || f?.type,
                      url: previewUrl || f?.url || f?.thumbUrl,
                      thumbUrl: previewUrl || f?.thumbUrl || f?.url,
                      isServer: false,
                      blob,
                      originFileObj: blob,
                      blobPath: f?.blobPath, // Keep blobPath for future matching
                      blobContainer: f?.blobContainer,
                    });
                  }

                  if (localList.length > 0) {
                    mergedLocal[itemId] = dedupeUploadList(localList);
                  }
                }

                if (Object.keys(mergedLocal).length === 0) return;

                // Add prefetched blobs to filesMap
                setFilesMap((prev) => {
                  const next = { ...(prev || {}) };
                  Object.entries(mergedLocal).forEach(([itemId, list]) => {
                    next[itemId] = dedupeUploadList([...(next[itemId] || []), ...(list || [])]);
                  });
                  return next;
                });

                // Remove prefetched server images from serverFileMap to avoid duplicates
                setServerFileMap((prev) => {
                  const next = { ...(prev || {}) };
                  Object.entries(mergedLocal).forEach(([itemId, prefetchedList]) => {
                    if (!next[itemId]) return;

                    // Build set of blobPaths that were prefetched
                    const prefetchedBlobPaths = new Set();
                    prefetchedList.forEach((pf) => {
                      if (pf.blobPath) prefetchedBlobPaths.add(pf.blobPath);
                    });

                    // Remove server images that were prefetched
                    next[itemId] = (next[itemId] || []).filter((serverImg) => {
                      return !prefetchedBlobPaths.has(serverImg.blobPath);
                    });

                    // Clean up empty arrays
                    if (next[itemId].length === 0) {
                      delete next[itemId];
                    }
                  });
                  return next;
                });

                // Persist immediately so offline mode has the blobs even before the user edits.
                setTimeout(() => {
                  try {
                    const snapshot = buildDraftSnapshotRef.current?.();
                    if (snapshot) {
                      Promise.resolve(saveInspectionDraft(snapshot))
                        .then((ok) => {
                          if (ok) {
                            hasLocalDraftRef.current = true;
                          }
                        })
                        .catch(() => { });
                    }
                  } catch { }
                }, 0);
              } catch (e) {
                console.warn('Failed to prefetch server images for offline:', e);
              }
            };

            prefetch();
          }
        }

        // Set company if available (but never overwrite an existing local selection)
        if (detail.companyId) {
          if (!selectedCompanyRef.current && !selectedCompany) setSelectedCompany(detail.companyId);
        }

      } catch (error) {
        console.error('Error loading inspection data:', error);
        message.error('Failed to load inspection data. Please try again.');
      }
    };

    loadInspectionData();
  }, [responseId, draftInitComplete]);

  // removed noisy serverFileMap debug logging

  // Memoize fetchTrucks to prevent it from being recreated on every render
  const fetchTrucksMemoized = useCallback(async (fleetId) => {
    if (!fleetId) {
      setTrucks([]);
      return;
    }

    try {
      setLoadingTrucks(true);
      const response = await getCompanyFleetTrucks(fleetId);

      // Handle different response structures
      let trucksArray = [];
      if (Array.isArray(response)) {
        trucksArray = response;
      } else if (response && Array.isArray(response.data)) {
        trucksArray = response.data;
      } else if (response && typeof response === 'object') {
        trucksArray = [response];
      }

      setTrucks(trucksArray);
    } catch (error) {
      console.error('Error fetching trucks:', error);
      message.error('Failed to load trucks. Please try again.');
      setTrucks([]);
    } finally {
      setLoadingTrucks(false);
    }
  }, []);

  // Handle fleet selection
  const handleFleetSelect = (fleetId) => {
    setSelectedFleetId(fleetId);

    // Find the selected fleet
    const selectedFleet = fleets.find(f => f.id === fleetId);
    if (!selectedFleet) return;

    // Find the fleet field in the template
    const fleetField = templateJson?.templates?.[0]?.FleetInformation?.find(
      field => field.title?.toLowerCase().includes('fleet')
    );

    if (fleetField?.itemId) {
      // Update the fleet field in the template
      updateItemResponse(fleetField.itemId, {
        text: selectedFleet.name || selectedFleet.id,
        optionId: selectedFleet.id,
        fleetInfo: {
          id: selectedFleet.id,
          name: selectedFleet.name || selectedFleet.id,
          // Include any other fleet-related data that might be needed
          ...(selectedFleet._original?.vehicleModel || {})
        }
      });
    }

    // Fetch trucks for the selected fleet
    fetchTrucks(fleetId);
  };

  // Fetch trucks when selected fleet changes
  useEffect(() => {
    if (selectedFleetId) {
      // Reset selected truck when fleet changes
      setSelectedTruck(null);
      fetchTrucksMemoized(selectedFleetId);
    } else {
      setTrucks([]);
      setSelectedTruck(null);
    }
  }, [selectedFleetId, fetchTrucksMemoized]);

  // Current items to display based on step
  const currentItems = useMemo(() => {
    return currentStep === 0 ? fleetInformation : inspectionQuestions;
  }, [currentStep, fleetInformation, inspectionQuestions]);

  // Update item response in the template JSON
  const updateItemResponse = (itemId, patch) => {
    const prev = templateRef.current;
    if (!prev?.templates?.[0]) return;

    const safePatch = { ...(patch || {}) };
    if (Object.prototype.hasOwnProperty.call(safePatch, 'text') && safePatch.text !== undefined && safePatch.text !== null) {
      safePatch.text = String(safePatch.text);
    }
    if (Object.prototype.hasOwnProperty.call(safePatch, 'comment') && safePatch.comment !== undefined && safePatch.comment !== null) {
      safePatch.comment = String(safePatch.comment);
    }

    const next = deepClone(prev);
    let itemFound = false;

    // Check FleetInformation first
    if (next.templates[0].FleetInformation) {

      const fleetIndex = next.templates[0].FleetInformation.findIndex(item => item.itemId === itemId);
      if (fleetIndex !== -1) {
        const currentResponse = next.templates[0].FleetInformation[fleetIndex].response || {};
        // For fleet items, make sure to preserve the fleetInfo structure
        const updatedResponse = { ...currentResponse };

        // If this is a fleet info update, ensure the response has the correct structure
        if (patch.fleetInfo) {
          // Create a new fleetInfo object that merges existing and new data
          const newFleetInfo = {
            ...(currentResponse.fleetInfo || {}),
            ...patch.fleetInfo
          };

          // Update the response with the merged fleetInfo
          updatedResponse.fleetInfo = newFleetInfo;

          // Preserve manual edits: only change text/optionId if explicitly provided
          if ('text' in patch) {
            updatedResponse.text = patch.text;
          } else if (typeof updatedResponse.text !== 'string') {
            updatedResponse.text = newFleetInfo.name || newFleetInfo.registration || '';
          }

          if ('optionId' in patch) {
            updatedResponse.optionId = patch.optionId;
          } else if (!updatedResponse.optionId) {
            updatedResponse.optionId = newFleetInfo.id || newFleetInfo.registration || '';
          }
        } else {
          // For non-fleet updates, merge the patch
          Object.assign(updatedResponse, safePatch);
        }

        next.templates[0].FleetInformation[fleetIndex].response = updatedResponse;

        const textValue = updatedResponse?.text === null || updatedResponse?.text === undefined ? '' : String(updatedResponse.text);
        const optionIdValue = updatedResponse?.optionId;
        if (textValue.trim().length > 0 || optionIdValue) {
          removeInvalid(`fleet-${itemId}`);
        }

        itemFound = true;
      }
    }

    // If not found in FleetInformation, check InspectionQuestions
    if (!itemFound && next.templates[0].InspectionQuestions) {
      const inspectionIndex = next.templates[0].InspectionQuestions.findIndex(item => item.itemId === itemId);
      if (inspectionIndex !== -1) {
        const currentItem = next.templates[0].InspectionQuestions[inspectionIndex];
        const currentResponse = currentItem.response || {};
        const newResponse = { ...currentResponse, ...safePatch };

        // Red/amber findings are only valid once their evidence has been supplied.
        if (
          currentItem.type !== 'checkbox'
          && ['red', 'amber'].includes(String(newResponse.color || '').toLowerCase())
        ) {
          const hasComment = newResponse.comment?.trim();
          const hasImages = hasItemImages(currentItem, filesMap, serverFileMap, newResponse);

          if (!hasComment || !hasImages) {
            newResponse.color = null;
          }
        }

        next.templates[0].InspectionQuestions[inspectionIndex] = {
          ...currentItem,
          response: newResponse,
        };

        // If this question is now valid, clear its highlight
        let isValidQuestion = true;
        if (currentItem.type === 'badge') {
          isValidQuestion = !!newResponse.color;
        } else if (currentItem.type === 'dropdown') {
          isValidQuestion = !!newResponse.optionId;
        } else if (currentItem.type === 'radio') {
          isValidQuestion = currentItem.required === false || !!newResponse.optionId;
        } else if (currentItem.type === 'checkbox') {
          isValidQuestion = true;
        } else if (currentItem.type === 'text') {
          isValidQuestion = !!newResponse.text && String(newResponse.text).trim().length > 0;
        }
        if (isValidQuestion) {
          removeInvalid(`question-${itemId}`);
        }
      }
    }
    templateRef.current = next;
    setTemplateJson(next);
    // Auto-save when inspection content changes
    debouncedSaveDraft();
  };

  const onFilesChange = (itemId, fileList) => {
    // fileList contains the new state of files for this item from the Upload component
    // We need to:
    // 1. Keep server images that are still in the list
    // 2. Handle new local images (add data URLs)
    // 3. Respect removals

    const enhanceWithDataUrls = async () => {
      const seenUids = new Set();
      const newFileUids = []; // UIDs that need background compression

      // ── Phase 1: Instant preview with original blobs (no compression) ──
      const enhanced = await Promise.all(
        (fileList || []).map(async (file) => {
          if (seenUids.has(file.uid)) return null;
          seenUids.add(file.uid);

          if (file.isServer) return file;

          const url = typeof file.url === 'string' ? file.url : '';
          const thumbUrl = typeof file.thumbUrl === 'string' ? file.thumbUrl : '';
          const hasPersistentUrl = (s) => s && !s.startsWith('blob:');

          let blob = file.originFileObj || file.blob || null;
          const alreadyCompressed = blob && compressedBlobsRef.current.has(file.uid);
          const alreadyPending = blob && pendingCompressionsRef.current.has(file.uid);

          if (blob && !alreadyCompressed && !alreadyPending) {
            // HEIC conversion only (fast for non-HEIC, required for HEIC preview)
            try {
              blob = (await convertHeicToJpegIfNeeded(blob)) || blob;
            } catch { /* keep original */ }
            newFileUids.push({ uid: file.uid, blob });
          }

          const isNewForCompression = newFileUids.some((n) => n.uid === file.uid);
          const needsPreviewUrl = !hasPersistentUrl(url) || !hasPersistentUrl(thumbUrl);
          const previewUrl = (() => {
            if (!needsPreviewUrl) return url || thumbUrl;
            if (!blob) return url || thumbUrl;
            // NEW files: don't decode full-res originals for preview — wait for
            // Phase 2 to create a preview from the compressed (small) blob.
            // This prevents 20 simultaneous full-res decodes from crashing the tab.
            if (isNewForCompression) return '';
            if (typeof URL === 'undefined' || !URL.createObjectURL) return url || thumbUrl;
            // Revoke previous blob URL for this uid before creating a new one
            const prevUrl = objectUrlsRef.current.get(file.uid);
            if (prevUrl) { try { URL.revokeObjectURL(prevUrl); } catch { } }
            try {
              const newUrl = URL.createObjectURL(blob);
              objectUrlsRef.current.set(file.uid, newUrl);
              return newUrl;
            } catch { return url || thumbUrl; }
          })();

          return {
            ...file,
            url: previewUrl || url,
            thumbUrl: previewUrl || thumbUrl,
            name: blob?.name || file.name,
            originFileObj: blob || file.originFileObj,
            blob,
            isServer: false,
          };
        })
      );

      const finalList = enhanced.filter(Boolean);

      const visibleServerList = dedupeUploadList(finalList.filter((f) => !!f?.isServer));
      const localList = dedupeUploadList(finalList.filter((f) => !f?.isServer));

      // IMPORTANT: fileList is a merged/deduped view (see mergeImageLists), so server images that
      // have local cached blob copies may not appear in fileList. Preserve those server images.
      const localBlobPaths = new Set((localList || []).map((f) => f?.blobPath).filter(Boolean));
      const visibleServerUids = new Set((visibleServerList || []).map((f) => f?.uid).filter(Boolean));
      const prevServerList = serverFileMap[itemId] || [];
      const preservedServerList = (prevServerList || []).filter((sf) => {
        if (!sf) return false;
        if (sf?.uid && visibleServerUids.has(sf.uid)) return true;
        if (sf?.blobPath && localBlobPaths.has(sf.blobPath)) return true;
        return false;
      });

      const serverList = dedupeUploadList([...(preservedServerList || []), ...(visibleServerList || [])]);

      setServerFileMap((prev) => ({ ...(prev || {}), [itemId]: serverList }));

      // Revoke blob URLs for files that were removed from this item
      const survivingUids = new Set((localList || []).map((f) => f?.uid).filter(Boolean));
      const prevLocalList = filesMap[itemId] || [];
      for (const f of prevLocalList) {
        if (f?.uid && !survivingUids.has(f.uid)) {
          const oldUrl = objectUrlsRef.current.get(f.uid);
          if (oldUrl) { try { URL.revokeObjectURL(oldUrl); } catch { } }
          objectUrlsRef.current.delete(f.uid);
          compressedBlobsRef.current.delete(f.uid);
        }
      }

      setFilesMap((prev) => {
        const newMap = { ...prev, [itemId]: localList };

        // If this is a red/amber badge and we've removed all images, clear the color
        const item = templateJson?.templates?.find(t => t.itemId === itemId);
        if (item && ['red', 'amber'].includes(item.response?.color) && (serverList.length + localList.length) === 0) {
          updateItemResponse(itemId, { color: null });
        }

        return newMap;
      });

      // Auto-save when images change (after URLs are present)
      debouncedSaveDraft();

      // ── Phase 2: Background compression (concurrency-limited, non-blocking) ──
      if (newFileUids.length > 0) {
        setProcessingImages(true);

        // Track which UIDs are compressing (for per-image spinners)
        const batchUidSet = new Set(newFileUids.map(({ uid }) => uid));
        setCompressingUids((prev) => new Set([...prev, ...batchUidSet]));

        // Track bulk progress for the full-screen overlay
        // Accumulate into the running total so overlapping batches don't reset each other
        bulkTotalRef.current += newFileUids.length;
        const snapshotTotal = bulkTotalRef.current;
        setBulkCompressionProgress((prev) => ({
          total: snapshotTotal,
          done: prev.total >= BULK_COMPRESSION_OVERLAY_THRESHOLD ? prev.done : 0,
        }));

        const tasks = newFileUids.map(({ uid, blob }) => async () => {
          let finalBlob = blob;
          try {
            finalBlob = await compressToWebp(blob, 0.1);
          } catch (e) {
            console.warn('[compressToWebp] background compression failed for', uid, e);
          }
          // Store in ref for collectLocalFiles lookup
          compressedBlobsRef.current.set(uid, finalBlob);
          // Create preview URL from the compressed (small) blob — safe to decode
          const prevUrl = objectUrlsRef.current.get(uid);
          if (prevUrl) { try { URL.revokeObjectURL(prevUrl); } catch { } }
          let previewUrl = '';
          try {
            previewUrl = URL.createObjectURL(finalBlob);
            objectUrlsRef.current.set(uid, previewUrl);
          } catch { }
          // Write compressed blob + preview back into filesMap so every copy of this
          // file gets the compressed version — prevents UID-mismatch duplicate uploads
          // and also drops the original blob reference.
          setFilesMap((prev) => {
            const next = { ...prev };
            for (const [key, list] of Object.entries(next)) {
              next[key] = (list || []).map((f) => {
                if (f.uid === uid) {
                  return {
                    ...f,
                    blob: finalBlob,
                    originFileObj: finalBlob,
                    url: previewUrl || f.url,
                    thumbUrl: previewUrl || f.thumbUrl,
                  };
                }
                return f;
              });
            }
            return next;
          });

          // Mark this UID as done compressing
          setCompressingUids((prev) => {
            const next = new Set(prev);
            next.delete(uid);
            return next;
          });
          setBulkCompressionProgress((prev) => {
            const nextDone = prev.done + 1;
            // If all images are done, reset so the overlay hides
            if (nextDone >= prev.total) {
              bulkTotalRef.current = 0;
              return { total: 0, done: 0 };
            }
            return { ...prev, done: nextDone };
          });
        });

        const batchPromise = (async () => {
          await runWithConcurrency(tasks, COMPRESSION_CONCURRENCY);
          // Clean up pending tracking for this batch
          for (const { uid } of newFileUids) {
            pendingCompressionsRef.current.delete(uid);
          }
          if (pendingCompressionsRef.current.size === 0) {
            setProcessingImages(false);
          }
          // If no more compressions pending globally, ensure overlay is hidden
          if (pendingCompressionsRef.current.size === 0) {
            bulkTotalRef.current = 0;
            setBulkCompressionProgress({ total: 0, done: 0 });
          }
        })();

        // Track each uid → batch promise so waitForCompressions can await them
        for (const { uid } of newFileUids) {
          pendingCompressionsRef.current.set(uid, batchPromise);
        }
      }
    };

    enhanceWithDataUrls();
  };

  const isImageFile = (file) => {
    if (!file) return false;
    if (typeof file.type === 'string' && file.type.startsWith('image/')) return true;
    const name = typeof file.name === 'string' ? file.name : '';
    if (!name) return false;
    return /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(name);
  };

  // Track pending files for multi-select summary modal
  const pendingUploadBatchRef = useRef({ valid: [], invalid: [], timer: null });

  // Background compression: store compressed blobs and track in-flight work
  const compressedBlobsRef = useRef(new Map());     // Map<uid, Blob>
  const pendingCompressionsRef = useRef(new Map());  // Map<uid, Promise>
  const objectUrlsRef = useRef(new Map());           // Map<uid, blobUrl> — for cleanup

  const waitForCompressions = async () => {
    if (pendingCompressionsRef.current.size === 0) return;
    message.loading({ content: 'Finishing image compression...', key: 'compress-wait', duration: 0 });
    await Promise.all(pendingCompressionsRef.current.values());
    message.destroy('compress-wait');
  };

  // Custom itemRender for Upload: shows a spinner overlay on images still being compressed
  const imageItemRender = (originNode, file, fileList, actions) => {
    const isCompressing = compressingUids.has(file.uid);
    if (!isCompressing) return originNode;
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        {originNode}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255,255,255,0.75)',
          borderRadius: 8,
          zIndex: 1,
        }}>
          <Spin size="small" />
        </div>
      </div>
    );
  };

  const showUploadSummaryModal = (valid, invalid) => {
    console.log('Setting modal state with valid:', valid.length, 'invalid:', invalid.length);
    setUploadSummaryModal({
      visible: true,
      valid: valid.map(f => f.name || 'Unnamed file'),
      invalid: invalid.map(f => f.name || 'Unnamed file'),
    });
  };

  const handleBeforeUploadImage = (file) => {
    const isImage = isImageFile(file);

    // Collect files into a batch; AntD calls beforeUpload once per file in rapid succession
    if (isImage) {
      pendingUploadBatchRef.current.valid.push(file);
    } else {
      pendingUploadBatchRef.current.invalid.push(file);
    }

    // Clear any existing timer and set a new one to show the modal after all files are processed
    if (pendingUploadBatchRef.current.timer) {
      clearTimeout(pendingUploadBatchRef.current.timer);
    }
    pendingUploadBatchRef.current.timer = setTimeout(() => {
      const { valid, invalid } = pendingUploadBatchRef.current;
      const totalFiles = valid.length + invalid.length;
      console.log('Timer fired - valid:', valid.length, 'invalid:', invalid.length, 'total:', totalFiles);

      if (invalid.length > 0) {
        // Always show modal for invalid files (single or multiple)
        console.log('Showing summary modal');
        showUploadSummaryModal([...valid], [...invalid]);
      }

      // Reset the batch
      pendingUploadBatchRef.current = { valid: [], invalid: [], timer: null };
    }, 50);

    if (!isImage) {
      return Upload.LIST_IGNORE;
    }

    // Prevent automatic upload; files are handled via onChange
    return false;
  };

  // Collect local files as raw File/Blob objects for SAS direct upload (no base64 conversion)
  // Prefers background-compressed blobs from compressedBlobsRef, falls back to original.
  const collectLocalFiles = async () => {
    const entries = Object.entries(filesMap);
    if (entries.length === 0) return [];

    const results = [];
    for (const [itemId, list] of entries) {
      const seenUids = new Set();
      for (const f of (list || [])) {
        if (!f) continue;
        if (f.isServer) continue;
        if (f.blobPath || f.uid?.startsWith('prefetch-')) continue;
        if (f.uid && seenUids.has(f.uid)) continue;
        if (f.uid) seenUids.add(f.uid);

        // Prefer the background-compressed blob if available
        const compressed = f.uid ? compressedBlobsRef.current.get(f.uid) : null;
        if (compressed instanceof Blob && compressed.size > 0) {
          results.push({ fieldKey: itemId, file: compressed, fileName: compressed.name || f.name || 'image.webp' });
          continue;
        }

        // Raw File/Blob from the file picker or draft restore (fallback — not yet compressed)
        const rawBlob = f.originFileObj || f.blob;
        if (rawBlob instanceof Blob && rawBlob.size > 0) {
          results.push({ fieldKey: itemId, file: rawBlob, fileName: rawBlob.name || f.name || 'image.webp' });
          continue;
        }

        // Cached data URL — convert back to Blob for direct upload
        const url = typeof f.url === 'string' ? f.url : null;
        const thumbUrl = typeof f.thumbUrl === 'string' ? f.thumbUrl : null;
        const candidate = url || thumbUrl;

        if (candidate && candidate.startsWith('data:')) {
          try {
            const resp = await fetch(candidate);
            const blob = await resp.blob();
            if (blob.size > 0) {
              results.push({ fieldKey: itemId, file: blob, fileName: blob.name || 'image.webp' });
            }
          } catch (e) {
            console.warn('Failed to convert data URL to blob for SAS upload:', e);
          }
        } else if (candidate && candidate.startsWith('blob:')) {
          try {
            const resp = await fetch(candidate);
            const blob = await resp.blob();
            if (blob.size > 0) {
              results.push({ fieldKey: itemId, file: blob, fileName: blob.name || 'image.webp' });
            }
          } catch (e) {
            console.warn('Failed to fetch blob URL for SAS upload:', e);
          }
        }
      }
    }

    // Safety dedup: prevent uploading the same image twice (e.g. original + compressed)
    const seen = new Set();
    return results.filter((r) => {
      const key = `${r.fieldKey}:${r.file.size}:${r.file.type || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const collectLocation = async () => {
    // Backend geography::Point cannot accept nulls, so always return numbers
    const fallback = { latitude: 0, longitude: 0 };
    if (!isGeolocationSupported()) return fallback;
    try {
      const { latitude, longitude } = await getCurrentLocation();
      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        return { latitude, longitude };
      }
      return fallback;
    } catch (e) {
      console.warn('collectLocation: failed to get geolocation', e);
      return fallback;
    }
  };

  const validateForm = (isDraft = false) => {
    const newInvalidFields = [];

    // For drafts, we're more lenient with validation
    if (isDraft) {
      setInvalidFields([]);
      return true;
    }

    // Check company selection
    if (!(selectedCompany || propCompanyId)) {
      newInvalidFields.push('company');
    }

    // Check vehicle selection if there are vehicles
    if (vehicles.length > 0 && !selectedVehicle?.registration) {
      newInvalidFields.push('vehicle');
    }

    // Check required questions
    const missingRequiredQuestions = [];
    const missingImageQuestions = [];
    const missingEvidenceQuestions = [];
    const questions = (() => {
      const tArr = Array.isArray(templateJson?.templates) ? templateJson.templates : [];
      if (tArr.length > 0) {
        const first = tArr[0];
        if (Array.isArray(first?.InspectionQuestions)) return first.InspectionQuestions;
        if (first && first.itemId) return tArr;
      }
      return Array.isArray(templateJson?.InspectionQuestions) ? templateJson.InspectionQuestions : [];
    })();
    questions.forEach((item) => {
      const isRequired = item.required !== false; // default to required
      const resp = item.response || {};
      const effectiveColor = item.type === 'checkbox'
        ? getChecklistResult(
          (Array.isArray(item?.config?.options) ? item.config.options : []).map(normalizeListOption),
          Array.isArray(resp.optionIds) ? resp.optionIds : [],
          item.required === false && resp.skipped === true
        ).color
        : resp.color;
      let isValid = true;

      if (isRequired) {
        if (item.type === 'badge') {
          isValid = !!resp.color;
        } else if (item.type === 'dropdown') {
          isValid = !!resp.optionId;
        } else if (item.type === 'radio') {
          isValid = !!resp.optionId;
        } else if (item.type === 'checkbox') {
          isValid = true;
        } else if (item.type === 'text') {
          isValid = !!resp.text && String(resp.text).trim().length > 0;
        }

        if (!isValid) {
          missingRequiredQuestions.push(`question-${item.itemId || item.id}`);
        }
      }

      if (
        !(item.type === 'checkbox' && resp.skipped === true)
        && itemRequiresImage(item)
        && !hasItemImages(item, filesMap, serverFileMap, resp)
      ) {
        missingImageQuestions.push(`question-${item.itemId || item.id}`);
      }
      if (
        ['red', 'amber'].includes(String(effectiveColor || '').toLowerCase()) &&
        (!String(resp.comment || '').trim() || !hasItemImages(item, filesMap, serverFileMap, resp))
      ) {
        missingEvidenceQuestions.push(`question-${item.itemId || item.id}`);
      }
    });

    // Combine all invalid fields
    const allInvalidFields = [...new Set([
      ...newInvalidFields,
      ...missingRequiredQuestions,
      ...missingImageQuestions,
      ...missingEvidenceQuestions,
    ])];
    setInvalidFields(allInvalidFields);

    // If there are validation errors
    if (allInvalidFields.length > 0) {
      // Show appropriate error message
      if (allInvalidFields.includes('company')) {
        message.error('Please select a company before submitting');
      } else if (allInvalidFields.includes('vehicle')) {
        message.error('Please select a vehicle before submitting');
      } else if (missingEvidenceQuestions.length > 0) {
        message.error(`Red and amber findings need a comment and at least one image (${missingEvidenceQuestions.length} remaining)`);
      } else if (missingImageQuestions.length > 0) {
        message.error(`Please add at least one image for required image fields (${missingImageQuestions.length} remaining)`);
      } else if (missingRequiredQuestions.length > 0) {
        message.error(`Please complete all required fields (${missingRequiredQuestions.length} remaining)`);
      }

      // Scroll to the first invalid field
      if (allInvalidFields.length > 0) {
        scrollToFirstInvalidField(allInvalidFields[0]);
      }

      return false;
    }

    // If we get here, validation passed
    setInvalidFields([]);
    return true;
  };

  // Add this function to handle scrolling to invalid fields
  const scrollToFirstInvalidField = (fieldId) => {
    setTimeout(() => {
      const element = document.querySelector(`[data-field-id="${fieldId}"]`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 100);
  };


  const submitInternal = async (isCompleted) => {
    const operation = isCompleted ? 'submit' : 'save draft';

    if (isCompleted) {
      setIsSubmittingCompleted(true);
    } else {
      setIsSubmittingDraft(true);
    }

    if (!templateId && !responseId) {
      const errorMsg = 'Missing template or response identifier';
      console.error(errorMsg, { templateId, responseId });
      message.error(errorMsg);
      setIsSubmittingCompleted(false);
      setIsSubmittingDraft(false);
      return false;
    }

    try {
      // Validate the form before submission
      // For drafts, we're more lenient with validation
      const isValid = validateForm(!isCompleted);

      if (!isValid) {
        const errorMsg = 'Form validation failed';
        message.warning('Please complete all required fields');
        setIsSubmittingCompleted(false);
        setIsSubmittingDraft(false);
        return false;
      }

      // Prevent saving an empty draft to avoid 400s from the backend (use same logic as button gating)
      if (!isCompleted) {
        if (!hasAnyInput) {
          message.warning('Nothing to save yet. Add a vehicle, answer a field, or attach an image.');
          setIsSubmittingCompleted(false);
          setIsSubmittingDraft(false);
          return false;
        }
      }

      // Validate template JSON
      if (!templateJson || !templateJson.templates || !Array.isArray(templateJson.templates) || templateJson.templates.length === 0) {
        message.error('Invalid inspection data. Please refresh and try again.');
        setIsSubmittingCompleted(false);
        setIsSubmittingDraft(false);
        return false;
      }

      // If this is a draft save and we are offline, save locally only
      if (!isCompleted && typeof navigator !== 'undefined' && !navigator.onLine) {
        const snapshot = buildDraftSnapshot();
        if (snapshot) {
          await saveInspectionDraft(snapshot);
          message.success('Draft saved locally for offline use');
        } else {
          message.warning('Nothing to save yet. Add a vehicle, answer a field, or attach an image.');
        }
        setIsSubmittingCompleted(false);
        setIsSubmittingDraft(false);
        return true;
      }

      setSubmitting(true);

      // Wait for any background image compressions to finish before collecting files
      await waitForCompressions();

      const localFiles = await collectLocalFiles();
      const uploadedFieldKeys = new Set((localFiles || []).map((img) => String(img?.fieldKey || '')).filter(Boolean));

      let latitude, longitude;
      try {
        const location = await collectLocation();
        latitude = location.latitude;
        longitude = location.longitude;
      } catch (locationError) {
        console.warn('Failed to get location, using default values', locationError);
        latitude = 0;
        longitude = 0;
      }

      const companyId = selectedCompany || propCompanyId || null;
      if (!companyId) {
        // Move user to the Fleet Information step so the company field is visible
        setCurrentStep(0);
        // Visually indicate the missing company even for drafts
        setInvalidFields(['company']);
        setTimeout(() => {
          scrollToFirstInvalidField('company');
        }, 50);
        message.error('Please select a company before saving.');
        setIsSubmittingCompleted(false);
        setIsSubmittingDraft(false);
        return false;
      }

      // Count badge types
      let redBadges = 0;
      let amberBadges = 0;
      let greenBadges = 0;

      const questions = (() => {
        const tArr = Array.isArray(templateJson?.templates) ? templateJson.templates : [];
        if (tArr.length > 0) {
          const first = tArr[0];
          if (Array.isArray(first?.InspectionQuestions)) return first.InspectionQuestions;
          if (first && first.itemId) return tArr;
        }
        return Array.isArray(templateJson?.InspectionQuestions) ? templateJson.InspectionQuestions : [];
      })();
      questions.forEach((item) => {
        if (item?.type === 'checkbox') {
          const options = (Array.isArray(item?.config?.options) ? item.config.options : [])
            .map(normalizeListOption);
          const checklist = getChecklistResult(
            options,
            Array.isArray(item?.response?.optionIds) ? item.response.optionIds : [],
            item?.response?.skipped === true
          );
          greenBadges += checklist.checkedCount;
          redBadges += checklist.uncheckedCount;
          return;
        }

        if (!item?.response?.color) return;

        switch (item.response.color.toLowerCase()) {
          case 'red':
            redBadges++;
            break;
          case 'amber':
            amberBadges++;
            break;
          case 'green':
            greenBadges++;
            break;
        }
      });

      const baseTemplate = templateRef.current || templateJson;
      const jsonToSave = deepClone(baseTemplate);

      if (!jsonToSave || !jsonToSave.templates || !Array.isArray(jsonToSave.templates)) {
        throw new Error('Invalid template format: missing templates array');
      }

      if (selectedVehicle) {
        // Derive latest manual edits from FleetInformation responses (make/model/year/vin/hours/odometer)
        const fiFields = Array.isArray(jsonToSave.templates?.[0]?.FleetInformation) ? jsonToSave.templates[0].FleetInformation : [];
        const latest = {};
        fiFields.forEach((f) => {
          const t = (f.title || '').toLowerCase();
          const txt = (f.response?.text ?? '').toString();
          if (t.includes('make')) latest.make = txt;
          else if (t.includes('model')) latest.model = txt;
          else if (t.includes('year')) latest.year = txt;
          else if (t.includes('vin')) latest.vin = txt;
          else if (t.includes('hours')) latest.currentHours = txt;
          else if (t.includes('odometer') || t.includes('km')) { latest.odometer = txt; latest.currentKm = txt; }
        });

        const fleetInfo = {
          id: selectedVehicle.id || selectedVehicle.fleet || '',
          registration: selectedVehicle.registration || '',
          fleet: selectedVehicle.fleet || '',
          make: ('make' in latest) ? latest.make : (selectedVehicle.make || ''),
          model: ('model' in latest) ? latest.model : (selectedVehicle.model || ''),
          year: ('year' in latest) ? latest.year : (selectedVehicle.year || ''),
          vin: ('vin' in latest) ? latest.vin : (selectedVehicle.vin || ''),
          currentHours: ('currentHours' in latest) ? latest.currentHours : (selectedVehicle.currentHours || 0),
          currentKm: ('currentKm' in latest) ? latest.currentKm : (selectedVehicle.odometer || selectedVehicle.currentKm || 0),
          ...(latest.odometer ? { odometer: latest.odometer } : {}),
        };

        if (jsonToSave.templates[0]?.FleetInformation) {
          jsonToSave.templates[0].FleetInformation = jsonToSave.templates[0].FleetInformation.map(field => {
            if (!field?.itemId) return field;

            const fieldTitle = field.title?.toLowerCase() || '';
            const isFleetField = fieldTitle.includes('fleet');
            const isRegistrationField = fieldTitle.includes('registration') || fieldTitle.includes('reg no') || fieldTitle.includes('regno') || fieldTitle.includes('rego') || fieldTitle.includes('license plate') || fieldTitle.includes('number plate');

            if (isFleetField || isRegistrationField) {
              const response = field.response || {};
              return {
                ...field,
                response: {
                  ...response,
                  fleetInfo: {
                    ...(response.fleetInfo || {}),
                    ...fleetInfo
                  },
                  text: isFleetField ? (selectedVehicle.fleet || response.text || '') :
                    (isRegistrationField ? (selectedVehicle.registration || response.text || '') : response.text)
                }
              };
            }
            return field;
          });
        }
      }

      if (jsonToSave.templates[0]?.FleetInformation) {
        jsonToSave.templates[0].FleetInformation = jsonToSave.templates[0].FleetInformation.map(field => {
          if (!field?.itemId) return field;
          const title = field.title?.toLowerCase() || '';
          if (title.includes('latitude')) {
            const response = field.response || {};
            return {
              ...field,
              response: {
                ...response,
                text: String(latitude ?? 0),
                optionId: String(latitude ?? 0),
              }
            };
          }
          if (title.includes('longitude')) {
            const response = field.response || {};
            return {
              ...field,
              response: {
                ...response,
                text: String(longitude ?? 0),
                optionId: String(longitude ?? 0),
              }
            };
          }
          return field;
        });
      }

      // Normalize option-based responses so saved drafts and submissions share one shape.
      if (jsonToSave.templates[0]?.InspectionQuestions) {
        jsonToSave.templates[0].InspectionQuestions = jsonToSave.templates[0].InspectionQuestions.map(field => {
          if (!field) return field;
          if (!['dropdown', 'radio', 'checkbox'].includes(field.type)) return field;
          const options = (Array.isArray(field?.config?.options) ? field.config.options : []).map(normalizeListOption);
          const resp = { ...(field.response || {}) };

          if (Object.prototype.hasOwnProperty.call(resp, 'colour')) {
            delete resp.colour;
          }

          if (field.type === 'checkbox') {
            const skipped = field.required === false && resp.skipped === true;
            const selectedIdSet = new Set(
              (Array.isArray(resp.optionIds) ? resp.optionIds : []).map(value => String(value))
            );
            const selected = skipped
              ? []
              : options.filter(option => selectedIdSet.has(String(option.id)));
            resp.optionIds = selected.map(option => option.id);
            resp.skipped = skipped;
            resp.text = skipped
              ? 'Skipped'
              : selected.map(option => option.label).join(', ');
            resp.color = getChecklistResult(options, resp.optionIds, skipped).color;
          } else {
            let selected = options.find(option => String(option.id) === String(resp.optionId));
            if (!selected && resp.text) {
              selected = options.find(option =>
                String(option.id) === String(resp.text) ||
                option.label === resp.text
              );
            }
            if (selected) {
              resp.optionId = selected.id;
              resp.text = selected.label;
              resp.color = selected.color || null;
            }
          }

          return { ...field, response: resp };
        });
      }
      if (jsonToSave.templates[0]?.InspectionQuestions) {
        jsonToSave.templates[0].InspectionQuestions = jsonToSave.templates[0].InspectionQuestions.map(field => {
          if (!field?.itemId) return field;
          const title = field.title?.toLowerCase() || '';
          if (title.includes('latitude')) {
            const response = field.response || {};
            return {
              ...field,
              response: {
                ...response,
                text: String(latitude ?? 0),
                optionId: String(latitude ?? 0),
              }
            };
          }
          if (title.includes('longitude')) {
            const response = field.response || {};
            return {
              ...field,
              response: {
                ...response,
                text: String(longitude ?? 0),
                optionId: String(longitude ?? 0),
              }
            };
          }
          return field;
        });
      }

      const sanitizedJson = stripClientPropsForSave(jsonToSave);
      if (!sanitizedJson) {
        const errorMsg = 'Failed to prepare inspection data for saving';
        console.error(errorMsg, { jsonToSave });
        throw new Error(errorMsg);
      }

      // Verify the sanitized JSON
      if (!sanitizedJson.templates || !Array.isArray(sanitizedJson.templates) || sanitizedJson.templates.length === 0) {
        console.error('Sanitized JSON is missing templates array:', sanitizedJson);
        throw new Error('Failed to prepare inspection data: invalid format after sanitization');
      }

      const now = new Date();
      const startOfYear = new Date(now.getFullYear(), 0, 1); // January 1st of current year
      const diffMs = now - startOfYear; // Difference in milliseconds
      const ans = Math.ceil(diffMs / 10); // Divide by 10 and round up
      const refId = ans.toString(16).toUpperCase(); // Convert to hexadecimal and make uppercase
      reference = `RNR${refId}-I`;

      // Prepare the base payload
      const base = {
        inspectionItemsJson: (() => {
          try {
            const jsonString = JSON.stringify(sanitizedJson);

            if (!jsonString || jsonString === '{}') {
              const errorMsg = 'Empty inspection data';
              console.error(errorMsg);
              throw new Error(errorMsg);
            }
            return jsonString;
          } catch (error) {
            console.error('Failed to stringify inspection data:', error);
            throw new Error('Failed to prepare inspection data: ' + error.message);
          }
        })(),
        latitude,
        longitude,
        companyId,
        redBadges,
        amberBadges,
        greenBadges,
        reference,
      };

      let result;
      // Use responseId from prop, or loadedResponseId from local draft
      const effectiveResponseId = responseId || loadedResponseId;

      // --- Helper: extract ID from any API response shape ---
      const extractId = (res) => {
        if (!res) return null;
        if (typeof res === 'string' || typeof res === 'number') return String(res);
        return (
          res.id || res.responseId || res.inspectionResponseId ||
          res.inspectionResponse?.id ||
          res.data?.id || res.data?.responseId || res.data?.inspectionResponseId ||
          res.data?.inspectionResponse?.id ||
          null
        );
      };

      // --- Helper: SAS direct upload (images go straight to Azure Blob) ---
      const uploadImagesSAS = async (inspectionId, files) => {
        const total = files.length;

        // Step 1: Request pre-signed SAS upload URLs from the API
        message.loading({ content: `Preparing upload for ${total} image${total > 1 ? 's' : ''}...`, key: 'image-upload', duration: 0 });
        const urlItems = files.map((f) => ({ fieldKey: f.fieldKey, fileName: f.fileName }));
        const sasSlots = await generateImageUploadUrls(inspectionId, urlItems);

        // Step 2: PUT each file directly to Azure Blob Storage (parallel, max 4 concurrent)
        const MAX_CONCURRENT = 4;
        let uploadedCount = 0;
        const queue = [...sasSlots];
        const fileByKey = new Map();
        files.forEach((f) => {
          const key = f.fieldKey;
          if (!fileByKey.has(key)) fileByKey.set(key, []);
          fileByKey.get(key).push(f);
        });

        const putBlob = async (slot) => {
          // Match slot back to the collected file by fieldKey (consume in order)
          const pool = fileByKey.get(slot.fieldKey) || [];
          const fileItem = pool.shift();
          if (!fileItem) throw new Error(`No file found for fieldKey ${slot.fieldKey}`);

          const res = await fetch(slot.uploadUrl, {
            method: 'PUT',
            headers: {
              'x-ms-blob-type': 'BlockBlob',
              'Content-Type': slot.contentType,
            },
            body: fileItem.file,
          });

          if (!res.ok) {
            throw new Error(`Blob upload failed for ${slot.imageId}: ${res.status} ${res.statusText}`);
          }

          uploadedCount++;
          message.loading({
            content: `Uploading images (${uploadedCount} of ${total})...`,
            key: 'image-upload',
            duration: 0,
          });
        };

        // Process uploads with concurrency limit
        const executing = new Set();
        for (const slot of queue) {
          const p = putBlob(slot).then(() => executing.delete(p));
          executing.add(p);
          if (executing.size >= MAX_CONCURRENT) {
            await Promise.race(executing);
          }
        }
        await Promise.all(executing);

        // Step 3: Confirm all uploads so the API commits metadata to DB
        const confirmItems = sasSlots.map((s) => ({ fieldKey: s.fieldKey, imageId: s.imageId }));
        await confirmImageUploads(inspectionId, confirmItems);

        message.destroy('image-upload');
      };

      // ============================================================
      // PHASE 1: Save / create the inspection data WITHOUT images
      // ============================================================
      try {
        if (effectiveResponseId) {
          // Update existing response (draft or re-save)
          result = await updateInspectionResponse(effectiveResponseId, {
            ...base,
            isCompleted: localFiles.length > 0 ? false : !!isCompleted,
          });
        } else {
          // Create new response
          const createPayload = {
            inspectionTemplateId: templateId,
            ...base,
            isCompleted: localFiles.length > 0 ? false : !!isCompleted,
          };
          console.log('Creating inspection response (no images):', createPayload);
          result = await createInspectionResponse(createPayload);

        }
      } catch (apiError) {
        // If this was a draft save and we hit a network error, fall back to local draft
        if (!isCompleted && (!apiError.response || apiError.code === 'ERR_NETWORK' || apiError.code === 'ECONNABORTED')) {
          const snapshot = buildDraftSnapshot();
          if (snapshot) {
            await saveInspectionDraft(snapshot);
            message.success('Network issue: draft saved locally');
            return true;
          }
        }
        throw apiError;
      }

      const id = effectiveResponseId || extractId(result);

      if (!id) {
        console.error('No ID returned from API response:', result);
        throw new Error('Failed to save: No response ID received');
      }

      // ============================================================
      // PHASE 2: Upload images directly to Azure Blob via SAS URLs
      // ============================================================
      if (localFiles.length > 0) {
        try {
          await uploadImagesSAS(id, localFiles);
        } catch (imgError) {
          // Images failed but the inspection data is saved — inform user
          console.error('Image upload failed after inspection save:', imgError);
          message.warning('Inspection saved but some images failed to upload. You can retry from your drafts.');
          // Don't throw — the data is persisted, images can be retried
        }
      }

      // ============================================================
      // PHASE 3: Mark as completed (if needed and not already done)
      // ============================================================
      if (isCompleted && localFiles.length > 0) {
        try {
          await updateInspectionResponse(id, {
            ...base,
            isCompleted: true,
          });
        } catch (completeError) {
          console.error('Failed to mark inspection as completed:', completeError);
          message.warning('Inspection and images saved, but could not mark as completed. Please try again from your drafts.');
        }
      }

      if (isCompleted && !pdfFiredIdsRef.current.has(id)) {
        pdfFiredIdsRef.current.add(id);
        getInspectionToPdf(id).catch((err) => console.error('PDF generation failed:', err));
      }
      // Sync local draft after successful server save
      const cacheKeyToDelete = stableDraftKeyRef.current || draftCacheKey;
      if (isCompleted) {
        draftFinalizedRef.current = true;
        skipDraftSaveOnUnmountRef.current = true;
        try {
          debouncedSaveDraft.cancel?.();
        } catch { }
        try {
          const keysToDelete = new Set([
            cacheKeyToDelete,
            draftCacheKey,
            draftId,
            propDraftKey,
            originalDraftKeyRef.current,
            `resp-${id}`,
            responseId ? `resp-${responseId}` : null,
            loadedResponseId ? `resp-${loadedResponseId}` : null,
          ].filter(Boolean).map(String));

          for (const k of keysToDelete) {
            try {
              const ok = await deleteInspectionDraft(k);
              if (!ok) {
                console.error('Failed to delete local inspection draft key after submit:', { key: k, ok: !!ok });
              }
            } catch (inner) {
              console.error('Failed to delete local inspection draft key after submit:', { key: k, message: inner?.message });
            }
          }
        } catch (e) {
          console.error('Failed to delete local inspection draft after submit:', e);
        }
      } else if (!isCompleted) {
        const snapshot = buildDraftSnapshot();
        if (snapshot) {
          const currentKey = stableDraftKeyRef.current || draftCacheKey;
          const originalKey = originalDraftKeyRef.current || currentKey;

          snapshot.responseId = id;
          snapshot.originalDraftKey = originalKey || '';
          snapshot.data = { ...(snapshot.data || {}), originalDraftKey: originalKey || '' };

          // Canonical key: there should be ONLY ONE local draft record per inspection.
          // For server-linked drafts we always use resp-<serverId>.
          const canonicalKey = id ? `resp-${id}` : (stableDraftKeyRef.current || draftCacheKey);
          const currentKeyStr = currentKey ? String(currentKey) : '';
          const canonicalKeyStr = canonicalKey ? String(canonicalKey) : '';

          try {
            const ok = await saveInspectionDraft({ ...snapshot, cacheKey: canonicalKeyStr, draftId: canonicalKeyStr });
            if (!ok) {
              console.error('Failed to sync canonical local draft after server draft save:', { cacheKey: canonicalKeyStr, ok: !!ok });
            }
          } catch (e) {
            console.error('Failed to sync canonical local draft after server draft save:', e);
          }

          // Delete any previous non-canonical draft key (e.g. INS-* created before server linking).
          if (currentKeyStr && canonicalKeyStr && currentKeyStr !== canonicalKeyStr) {
            try {
              await deleteInspectionDraft(currentKeyStr);
            } catch (e) {
              console.error('Failed to delete previous local draft key after canonicalization:', e);
            }
          }

          // If route responseId differs from returned id, delete the alternate resp-* key to avoid duplicates.
          const alternateRespKey = responseId ? `resp-${responseId}` : null;
          if (alternateRespKey && alternateRespKey !== canonicalKeyStr) {
            try {
              await deleteInspectionDraft(alternateRespKey);
            } catch (e) {
              console.error('Failed to delete alternate resp-* draft key:', e);
            }
          }

          stableDraftKeyRef.current = canonicalKeyStr;
          setDraftId(canonicalKeyStr);
          setDraftCacheKey(canonicalKeyStr);
          setLoadedResponseId(id);
        }

        // After a successful SERVER draft save, rehydrate from the API so the server-draft UI
        // reflects exactly what the backend has (and so local image entries don't linger and
        // appear duplicated).
        try {
          if (typeof navigator === 'undefined' || navigator.onLine) {
            const refreshed = await getInspectionResponseDetail(id, true);
            const refreshedJson = refreshed?.inspectionItemsJson ? safeParse(refreshed.inspectionItemsJson) : null;
            if (refreshedJson) {
              const withCounts = applyServerImageCounts(refreshedJson, Array.isArray(refreshed?.images) ? refreshed.images : []);
              setTemplateJson(withCounts);
              templateRef.current = withCounts;
            }
            if (Array.isArray(refreshed?.images)) {
              const nextServer = imagesArrayToUploadMap(refreshed.images);
              const normalized = {};
              Object.entries(nextServer || {}).forEach(([k, list]) => {
                normalized[k] = dedupeUploadList(list);
              });

              // Build a set of all server blobPaths that currently exist
              const currentServerBlobPaths = new Set();
              Object.values(normalized || {}).forEach((list) => {
                (list || []).forEach((img) => {
                  if (img?.blobPath) currentServerBlobPaths.add(img.blobPath);
                });
              });

              // After save, sync filesMap and serverFileMap with the authoritative server state.
              // This is the single source of truth reconciliation.

              // Step 1: Remove local blob copies of images that no longer exist on server (were deleted)
              setFilesMap((prev) => {
                const next = { ...(prev || {}) };
                Object.entries(next).forEach(([k, list]) => {
                  if (!list || list.length === 0) return;
                  next[k] = list.filter((localFile) => {
                    // Keep files that don't have blobPath (truly local, not prefetched)
                    if (!localFile?.blobPath) return true;
                    // Keep prefetched files only if their server image still exists
                    return currentServerBlobPaths.has(localFile.blobPath);
                  });
                });
                return next;
              });

              // Step 2: Completely replace serverFileMap with the server's current state.
              // Don't merge - replace. This ensures we don't accumulate stale entries.
              setServerFileMap(normalized);

              // If the server returned images for fields we just uploaded, clear ALL local copies
              // for those fields to avoid duplicates. The server now has the authoritative copy.
              if (uploadedFieldKeys.size > 0) {
                const keysWithServerImages = new Set(
                  Object.entries(normalized || {})
                    .filter(([, list]) => Array.isArray(list) && list.length > 0)
                    .map(([k]) => String(k))
                );
                setFilesMap((prev) => {
                  const next = { ...(prev || {}) };
                  keysWithServerImages.forEach((k) => {
                    if (uploadedFieldKeys.has(k)) {
                      // Clear all local images for this field since we just uploaded them
                      next[k] = [];
                    }
                  });
                  return next;
                });
              }
            }
          }
        } catch (e) {
          console.error('Failed to rehydrate after save:', e);
        }
      }

      // Show success and let parent handle navigation/state via onSaved
      message.success(isCompleted ? 'Inspection submitted successfully!' : 'Draft saved successfully!');
      onSaved?.(id, { isCompleted });
      return true;
    } catch (e) {
      console.error('Submit inspection failed:', e);
      const serverErrors = e.response?.data?.errors;
      let errorMessage = e.response?.data?.message || e.message || 'Failed to submit inspection';

      if (e.response?.status === 400 && !isCompleted) {
        const hasUsefulServerMessage = typeof e.response?.data?.message === 'string' && e.response.data.message.trim().length > 0;
        const hasUsefulErrors = serverErrors && typeof serverErrors === 'object' && Object.keys(serverErrors).length > 0;
        if (!hasUsefulServerMessage && !hasUsefulErrors) {
          errorMessage = 'Nothing to save yet. Add a vehicle, answer a field, or attach an image.';
        }
      }
      if (serverErrors && typeof serverErrors === 'object') {
        const details = Object.entries(serverErrors)
          .map(([field, errs]) => `${field}: ${Array.isArray(errs) ? errs.join(', ') : String(errs)}`)
          .join('; ');
        errorMessage = `Validation failed: ${details}`;
      }
      console.error('Error details:', {
        message: e.message,
        status: e.response?.status,
        response: e.response?.data,
        stack: e.stack
      });
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
      setIsSubmittingDraft(false);
      setIsSubmittingCompleted(false);
    }
  };

  const handleBadgeChange = (item, newColor) => {
    // If clicking the same color, don't do anything
    if (item.response?.color === newColor) {
      return;
    }

    // Check if we need to show the modal
    const hasComment = !!item.response?.comment?.trim();
    const hasImages = hasItemImages(item, filesMap, serverFileMap);

    const updatedItem = {
      ...item,
      response: {
        ...item.response,
        color: newColor
      }
    };

    if (['red', 'amber'].includes(newColor) && (!hasComment || !hasImages)) {
      setCurrentBadgeItem(updatedItem);
      // Update form values immediately
      badgeForm.setFieldsValue({
        comment: updatedItem.response?.comment || ''
      });
      setBadgeModalVisible(true);
    } else {
      // For green or when all required info is already provided
      updateItemResponse(item.itemId, updatedItem.response);
    }
  };

  const handleBadgeModalOk = async () => {
    try {
      // Validate using the badge modal's own form instance
      const formInstance = badgeForm;
      if (!formInstance) {
        console.error('Form instance not found');
        return;
      }

      // Validate form fields
      const values = await formInstance.validateFields();
      const comment = values?.comment || '';

      if (!currentBadgeItem?.itemId) {
        console.error('No currentBadgeItem or itemId found');
        return;
      }

      const hasImages = hasItemImages(currentBadgeItem, filesMap, serverFileMap);

      // For red/amber badges, require both comment and image
      if (['red', 'amber'].includes(currentBadgeItem.response?.color)) {
        if (!comment.trim() && !hasImages) {
          message.error('Please provide both a comment and at least one image');
          return;
        }
        if (!comment.trim()) {
          message.error('Please provide a comment');
          return;
        }
        if (!hasImages) {
          message.error('Please add at least one image');
          return;
        }
      }

      // Update the item with the new comment and color
      updateItemResponse(currentBadgeItem.itemId, {
        ...currentBadgeItem.response,
        comment: comment.trim()
      });

      // Close modal and reset state
      setBadgeModalVisible(false);
      setCurrentBadgeItem(null);

    } catch (error) {
      // Don't show error if validation failed (Ant Design handles this)
      if (error.errorFields) return;

      console.error('Failed to save badge information:', error);
      message.error('Failed to save. Please try again.');
    }
  };

  const handleBadgeModalCancel = () => {
    // Reset the badge color if user cancels without saving
    if (currentBadgeItem?.itemId) {
      const originalItem = templateJson?.templates?.find(
        t => t.itemId === currentBadgeItem.itemId
      );

      if (originalItem?.response?.color !== currentBadgeItem.response?.color) {
        // Revert to the original color if it was changed
        updateItemResponse(currentBadgeItem.itemId, {
          ...originalItem?.response,
          color: originalItem?.response?.color || null
        });
      }
    }

    // Reset only the badge modal form and close
    badgeForm.resetFields();
    setBadgeModalVisible(false);
    setCurrentBadgeItem(null);
  };

  // Update form values whenever currentBadgeItem changes
  useEffect(() => {
    if (currentBadgeItem) {
      badgeForm.setFieldsValue({
        comment: currentBadgeItem.response?.comment || ''
      });
    } else {
      badgeForm.resetFields();
    }
  }, [currentBadgeItem, badgeForm]);

  // Reset the badge modal form when the modal opens
  useEffect(() => {
    if (badgeModalVisible && currentBadgeItem) {
      badgeForm.setFieldsValue({
        comment: currentBadgeItem.response?.comment || ''
      });
    }
  }, [badgeModalVisible, currentBadgeItem, badgeForm]);

  const renderBadgeModal = () => {
    // Calculate if save button should be disabled (no hooks)
    const isSaveDisabled = () => {
      if (!badgeModalVisible) return true;
      const comment = badgeCommentValue || '';
      const hasImgs = (filesMap[currentBadgeItem?.itemId]?.length || 0) +
        (serverFileMap[currentBadgeItem?.itemId]?.length || 0) > 0;
      return !comment.trim() || !hasImgs;
    };

    return (
      <Modal
        title="Additional Information Required"
        open={badgeModalVisible}
        onOk={handleBadgeModalOk}
        onCancel={handleBadgeModalCancel}
        okText="Save"
        cancelText="Cancel"
        okButtonProps={{
          disabled: isSaveDisabled()
        }}
        width={600}
        maskClosable={false}
        destroyOnClose
      >
        <p><ExclamationCircleFilled style={{ color: '#faad14', marginRight: 8 }} />
          You've selected {currentBadgeItem?.response?.color == 'red' ? 'a red' : 'an amber'} finding. Please provide a comment and at least one image.
        </p>
        <Form
          form={badgeForm}
          layout="vertical"
          initialValues={{ comment: currentBadgeItem?.response?.comment || '' }}
        >
          <Form.Item
            name="comment"
            label="Comment"
            rules={[{ required: true, message: 'Please provide a comment for this issue' }]}
          >
            <TextArea
              rows={4}
              placeholder="Please explain the issue..."
              onChange={() => { /* re-render occurs via form control */ }}
              disabled={!editable}
            />
          </Form.Item>
          <div>
            <Text strong>Images</Text>{processingImages && <span style={{ marginLeft: 8 }}><Spin size="small" /> <Text type="secondary" style={{ fontSize: 12 }}>Compressing...</Text></span>}
            <Upload
              listType="picture-card"
              multiple
              accept="image/*,android/allowCamera"
              beforeUpload={handleBeforeUploadImage}
              itemRender={imageItemRender}
              fileList={[
                ...(serverFileMap[currentBadgeItem?.itemId] || []),
                ...(filesMap[currentBadgeItem?.itemId] || [])
              ].length ? dedupeUploadList([
                ...(serverFileMap[currentBadgeItem?.itemId] || []),
                ...(filesMap[currentBadgeItem?.itemId] || [])
              ]) : []}
              onChange={({ fileList }) => onFilesChange(currentBadgeItem.itemId, fileList)}
              onPreview={handlePreview}
              onRemove={async (file) => {
                if (file.isServer) {
                  const success = await handleRemove(file);
                  if (success) {
                    // Remove from serverFileMap
                    const updatedServerFiles = { ...serverFileMap };
                    const id = currentBadgeItem.itemId;
                    if (updatedServerFiles[id]) {
                      updatedServerFiles[id] = updatedServerFiles[id].filter(
                        f => f.uid !== file.uid
                      );
                      setServerFileMap(updatedServerFiles);

                      // Check if this was the last image and badge is red/amber
                      const totalImages = (updatedServerFiles[id]?.length || 0) + (filesMap[id]?.length || 0);
                      const currentItem = templateJson?.templates?.[0]?.InspectionQuestions?.find(q => q.itemId === id);
                      const badgeColor = currentItem?.response?.color;

                      if (totalImages === 0 && ['red', 'amber'].includes(badgeColor)) {
                        // Reset the badge selection
                        updateItemResponse(id, {
                          ...currentItem.response,
                          color: null,
                          comment: currentBadgeItem?.response?.comment || ''
                        });
                      }
                    }
                  }
                  return success;
                } else {
                  // For non-server files, check if this is the last image after removal
                  const id = currentBadgeItem.itemId;
                  const updatedFiles = (filesMap[id] || []).filter(f => f.uid !== file.uid);
                  const totalImages = (serverFileMap[id]?.length || 0) + updatedFiles.length;
                  const currentItem = templateJson?.templates?.[0]?.InspectionQuestions?.find(q => q.itemId === id);
                  const badgeColor = currentItem?.response?.color;

                  if (totalImages === 0 && ['red', 'amber'].includes(badgeColor)) {
                    // Reset the badge selection
                    updateItemResponse(id, {
                      ...currentItem.response,
                      color: null,
                      comment: currentBadgeItem?.response?.comment || ''
                    });
                  }

                  return true;
                }
              }}
              style={{ margin: '8px 0px 0px 0px', width: '48px', height: '48px' }}
              disabled={!editable}
            >
              <div>
                <UploadOutlined />
              </div>
            </Upload>
            {(() => {
              const id = currentBadgeItem?.itemId;
              const total = dedupeUploadList([...(serverFileMap[id] || []), ...(filesMap[id] || [])]).length;
              return total > 0 ? (
                <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                  Uploaded images: {total}
                </Text>
              ) : null;
            })()}
          </div>
        </Form>
      </Modal>
    );
  }

  const renderItem = (item) => {
    const { itemId, type, title, config = {}, response = {} } = item;

    // Function to render label with required indicator
    const renderLabel = (label, required = true) => {
      const raw = String(label ?? '');
      const cleaned = raw.replace(/\s*\*\s*$/, '').trim();
      return (
        <span>
          {cleaned}
          {required ? <span style={{ color: 'red', marginLeft: 4 }}>*</span> : null}
        </span>
      );
    };

    // Special handling for fleet information fields
    if (currentStep === 0) {

      // Get all fleet information fields from the template
      const fleetInfoFields = templateJson?.templates?.[0]?.FleetInformation || [];

      // Find the current field index
      const currentFieldIndex = fleetInfoFields.findIndex(field => field.itemId === itemId);

      // Only render the card for the first fleet information field to avoid duplicates
      if (currentFieldIndex === 0) {
        // Filter out registration and fleet fields (we render custom inputs above)
        const excludedFields = ['registration', 'reg', 'number plate', 'license plate', 'fleet', 'fleet number'];
        const displayFields = fleetInfoFields.filter(field => {
          const fieldTitle = field.title?.toLowerCase() || '';
          return !excludedFields.some(excluded => fieldTitle.includes(excluded));
        });

        // Group all fleet information in a single card
        return (
          <Card key="fleet-info-card" size="small" style={{ marginBottom: 16 }}>
            <Title level={4} style={{ marginBottom: 16 }}>Vehicle Information</Title>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: 16 }}>
              {/* Company Selection */}
              <div style={{ width: '100%' }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Company <Text type="danger">*</Text></Text>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={options}
                  placeholder={companies.length === 1 ? companies[0].name : 'Select a company'}
                  style={{ width: '100%' }}
                  loading={loadingCompanies}
                  value={selectedId}
                  disabled={companies.length === 1 || !editable}
                  status={invalidFields.includes('company') ? 'error' : undefined}
                  notFoundContent={loadingCompanies ? 'Loading...' : 'No companies found'}
                  onChange={(value) => {
                    setInvalidFields([]);
                    handleCompanyChange(value);
                  }}
                />
              </div>

              {/* Vehicle Search */}
              <div style={{ width: '100%' }} data-field-id="vehicle">
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Vehicle Search <Text type="danger">*</Text></Text>
                <AutoComplete
                  style={{ width: '100%' }}
                  placeholder={selectedCompany ? "Search by registration, fleet, or VIN" : "Select a company first"}
                  onSearch={selectedCompany ? (value) => {
                    setSearchTerm(value);
                    setInputValue(value);
                    if (!value) {
                      handleVehicleSelect(null);
                      setVehicles([]);
                      setVehiclePageIndex(0);
                      setVehicleHasMore(false);
                    } else {
                      debouncedSearch(value);
                    }
                  } : undefined}
                  onChange={(value, option) => {
                    setInputValue(value);
                    if (option?.vehicle) {
                      handleVehicleSelect(option.vehicle);
                      removeInvalid('vehicle');
                      removeInvalid('truck-registration');
                      removeInvalid('fleet-number');
                    } else if (!value) {
                      handleVehicleSelect(null);
                    }
                  }}
                  onClear={clearVehicleFields}
                  value={inputValue}
                  notFoundContent={selectedCompany ? (vehicleLoading ? 'Searching...' : (searchTerm ? 'No vehicles found' : 'Start typing to search')) : 'Select a company to search vehicles'}
                  disabled={!selectedCompany || !editable}
                  allowClear={!!selectedCompany}
                  status={invalidFields.includes('vehicle') ? 'error' : undefined}
                  onPopupScroll={(e) => {
                    const { target } = e;
                    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 20) {
                      loadMoreVehicles();
                    }
                  }}
                >
                  {filteredVehicles.map(vehicle => {
                    const displayValue = vehicle.registration || vehicle.fleet || vehicle.vin || '';
                    return (
                      <AutoComplete.Option
                        key={displayValue}
                        value={displayValue}
                        vehicle={vehicle}
                      >
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {vehicle.registration} - {vehicle.make} {vehicle.model} {vehicle.year ? `(${vehicle.year})` : ''}                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {vehicle.fleet && <span>Fleet: {vehicle.fleet} • </span>}
                            {vehicle.vin && <span>VIN: {vehicle.vin}</span>}
                          </div>
                        </div>
                      </AutoComplete.Option>
                    );
                  })}
                  {vehicleHasMore && (
                    <AutoComplete.Option key="__loading_more__" value="__loading_more__" disabled>
                      <div style={{ textAlign: 'center', padding: '4px 0', color: '#999', fontSize: '12px' }}>
                        {vehicleLoading ? 'Loading more...' : 'Scroll for more'}
                      </div>
                    </AutoComplete.Option>
                  )}
                </AutoComplete>
                {invalidFields.includes('vehicle') && (
                  <div className="ant-form-item-explain ant-form-item-explain-error" style={{ fontSize: '11px', color: '#ff4d4f' }}>
                    * Please select a vehicle
                  </div>
                )}
              </div>

              {/* Fleet Number - Always Editable */}
              <div
                style={{
                  width: '100%',
                }}
                data-field-id="fleet-number"
              >
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Fleet Number <Text type="danger">*</Text></Text>
                <Input
                  placeholder="Enter fleet number"
                  value={selectedVehicle?.fleet ?? ''}
                  onChange={(e) => {
                    removeInvalid('fleet-number');
                    const newFleetValue = e.target.value;

                    // Create or update the selected vehicle with the new fleet value
                    setSelectedVehicle(prev => ({
                      ...(prev || {}),
                      fleet: newFleetValue,
                      // Ensure we have at least an empty object for _original if it doesn't exist
                      _original: {
                        ...(prev?._original || {}),
                        fleet: newFleetValue
                      }
                    }));

                    // Find all fleet-related fields in the template
                    const fleetFields = templateJson?.templates?.[0]?.FleetInformation?.filter(
                      field => field.title?.toLowerCase().includes('fleet')
                    ) || [];

                    // Update all fleet fields in the template
                    fleetFields.forEach(field => {
                      if (field?.itemId) {
                        // Get the current field response or create a new one
                        const currentResponse = field.response || {};
                        const currentFleetInfo = currentResponse.fleetInfo || {};

                        updateItemResponse(field.itemId, {
                          ...currentResponse,
                          text: newFleetValue,
                          optionId: newFleetValue,
                          fleetInfo: {
                            ...currentFleetInfo,
                            id: newFleetValue || currentFleetInfo.id || '',
                            name: newFleetValue || currentFleetInfo.name || '',
                            fleet: newFleetValue,
                            // Preserve any existing manual edits; only fall back to selectedVehicle when absent
                            registration: (currentFleetInfo.registration !== undefined ? currentFleetInfo.registration : (selectedVehicle?.registration || '')),
                            make: (currentFleetInfo.make !== undefined ? currentFleetInfo.make : (selectedVehicle?.make || '')),
                            model: (currentFleetInfo.model !== undefined ? currentFleetInfo.model : (selectedVehicle?.model || '')),
                            year: (currentFleetInfo.year !== undefined ? currentFleetInfo.year : (selectedVehicle?.year || '')),
                            vin: (currentFleetInfo.vin !== undefined ? currentFleetInfo.vin : (selectedVehicle?.vin || '')),
                            currentHours: (currentFleetInfo.currentHours !== undefined ? currentFleetInfo.currentHours : (selectedVehicle?.currentHours || 0)),
                            currentKm: (currentFleetInfo.currentKm !== undefined ? currentFleetInfo.currentKm : (selectedVehicle?.odometer || selectedVehicle?.currentKm || 0)),
                            ...(currentFleetInfo.odometer !== undefined ? { odometer: currentFleetInfo.odometer } : (selectedVehicle?.odometer ? { odometer: selectedVehicle.odometer } : {}))
                          }
                        });
                      }
                    });

                    // Also update the registration field's fleetInfo so selectedVehicle isn't reset from template effect
                    const registrationField = templateJson?.templates?.[0]?.FleetInformation?.find(
                      field => {
                        const t = field.title?.toLowerCase() || '';
                        return t.includes('registration') || t.includes('reg no') || t.includes('regno') || t.includes('rego') || t.includes('license plate') || t.includes('number plate');
                      }
                    );
                    if (registrationField?.itemId) {
                      const currentRegResponse = registrationField.response || {};
                      updateItemResponse(registrationField.itemId, {
                        fleetInfo: {
                          ...(currentRegResponse.fleetInfo || {}),
                          fleet: newFleetValue
                        }
                      });
                    }
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: '#fff'
                  }}
                  disabled={!editable}
                  status={invalidFields.includes('fleet-number') ? 'error' : undefined}
                />
                {invalidFields.includes('fleet-number') && (
                  <div className="ant-form-item-explain ant-form-item-explain-error" style={{ fontSize: '11px', color: '#ff4d4f' }}>
                    * Please enter fleet number
                  </div>
                )}
              </div>

              {/* Truck Registration Number */}
              <div
                style={{
                  width: '100%',
                }}
                data-field-id="truck-registration"
              >
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Truck Registration <Text type="danger">*</Text></Text>
                <Input
                  placeholder="Enter registration number"
                  value={selectedVehicle?.registration || ''}
                  onChange={(e) => {
                    removeInvalid('truck-registration');
                    removeInvalid('vehicle');
                    const newRegValue = e.target.value;

                    // Update local state
                    setSelectedVehicle(prev => ({
                      ...(prev || {}),
                      registration: newRegValue
                    }));

                    // Find the registration field in the template (explicit registration-like titles only)
                    const registrationField = templateJson?.templates?.[0]?.FleetInformation?.find(
                      field => {
                        const t = field.title?.toLowerCase() || '';
                        return t.includes('registration') || t.includes('reg no') || t.includes('regno') || t.includes('rego') || t.includes('license plate') || t.includes('number plate');
                      }
                    );

                    if (registrationField?.itemId) {
                      // Update the registration field in the template
                      const currentRegResponse = registrationField.response || {};
                      updateItemResponse(registrationField.itemId, {
                        text: newRegValue,
                        optionId: newRegValue,
                        fleetInfo: {
                          ...(currentRegResponse.fleetInfo || {}),
                          id: selectedVehicle?.id || newRegValue,
                          registration: newRegValue,
                          // preserve existing make/model/year/vin/current fields
                        }
                      });
                    }

                    // Also update the fleet field with the new registration if it exists
                    const fleetField = templateJson?.templates?.[0]?.FleetInformation?.find(
                      field => field.title?.toLowerCase().includes('fleet')
                    );

                    if (fleetField?.itemId) {
                      const currentFleetResponse = fleetField.response || {};
                      updateItemResponse(fleetField.itemId, {
                        ...currentFleetResponse,
                        fleetInfo: {
                          ...(currentFleetResponse.fleetInfo || {}),
                          registration: newRegValue
                        }
                      });
                    }
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: '#fff'
                  }}
                  disabled={!editable}
                  status={invalidFields.includes('truck-registration') ? 'error' : undefined}
                />
                {invalidFields.includes('truck-registration') && (
                  <div className="ant-form-item-explain ant-form-item-explain-error" style={{ fontSize: '11px', color: '#ff4d4f' }}>
                    * Please enter truck registration
                  </div>
                )}
              </div>


              {/* Dynamic fields from template */}
              {displayFields.map(field => {
                const fieldResponse = field.response || {};
                const fieldId = field.itemId;
                const fieldTitle = field.title || 'Untitled Field';

                // Get the current field value from the template response
                const fieldValue = fieldResponse.text === null || fieldResponse.text === undefined ? '' : String(fieldResponse.text);

                // Check if this is a vehicle info field that should be updated from fleetInfo
                const isMakeField = fieldTitle.toLowerCase().includes('make');
                const isModelField = fieldTitle.toLowerCase().includes('model');
                const isVehicleField = isMakeField || isModelField ||
                  fieldTitle.toLowerCase().includes('year') ||
                  fieldTitle.toLowerCase().includes('vin') ||
                  fieldTitle.toLowerCase().includes('hours') ||
                  fieldTitle.toLowerCase().includes('odometer') ||
                  fieldTitle.toLowerCase().includes('km');

                // Get the fleetInfo from the selected vehicle or registration field's response
                let fleetInfo = selectedVehicle ? {
                  reg: selectedVehicle.registration || '',
                  fleet: selectedVehicle.fleet || '',
                  make: selectedVehicle.make || '',
                  model: selectedVehicle.model || '',
                  year: selectedVehicle.year || '',
                  vin: selectedVehicle.vin || '',
                  currentHours: selectedVehicle.currentHours ?? 0,
                  currentKm: (selectedVehicle.odometer ?? selectedVehicle.currentKm) ?? 0,
                  odometer: selectedVehicle.odometer ?? 0
                } : null;

                // If no vehicles are available for the company, allow manual entry
                const isManualEntry = selectedCompany && vehicles.length === 0 && !selectedVehicle;

                // If no fleetInfo from selectedVehicle, try to get it from the registration field
                if (!fleetInfo) {
                  const registrationField = templateJson?.templates?.[0]?.FleetInformation?.find(item => {
                    const t = item?.title?.toLowerCase() || '';
                    return t.includes('registration') || t.includes('reg no') || t.includes('regno') || t.includes('rego') || t.includes('license plate') || t.includes('number plate');
                  });
                  fleetInfo = registrationField?.response?.fleetInfo;
                }

                // If still no fleetInfo, fall back to selectedTruck
                if (!fleetInfo && selectedTruck) {
                  fleetInfo = {
                    reg: selectedTruck.registration || selectedTruck.reg || '',
                    fleet: selectedTruck.fleet || '',
                    make: selectedTruck.make || selectedTruck._original?.make || '',
                    model: selectedTruck.model || selectedTruck._original?.model || '',
                    year: selectedTruck.year || selectedTruck._original?.year || '',
                    vin: selectedTruck._original?.vin || '',
                    currentHours: selectedTruck.currentHours || 0,
                    currentKm: selectedTruck.currentKm || 0,
                  };
                }

                // Get the value from fleetInfo if available, otherwise use the field's current value
                let displayValue = fieldValue;

                if (isVehicleField && fleetInfo) {
                  if (fieldTitle.toLowerCase().includes('make')) {
                    displayValue = fieldValue || fleetInfo.make || '';
                  } else if (fieldTitle.toLowerCase().includes('model')) {
                    displayValue = fieldValue || fleetInfo.model || '';
                  } else if (fieldTitle.toLowerCase().includes('year')) {
                    displayValue = fieldValue || String(fleetInfo.year ?? '');
                  } else if (fieldTitle.toLowerCase().includes('vin')) {
                    displayValue = fieldValue || fleetInfo.vin || '';
                  } else if (fieldTitle.toLowerCase().includes('hours')) {
                    displayValue = fieldValue || String(fleetInfo.currentHours ?? '');
                  } else if (fieldTitle.toLowerCase().includes('odometer') || fieldTitle.toLowerCase().includes('km')) {
                    displayValue = fieldValue || String((fleetInfo.odometer ?? fleetInfo.currentKm) ?? '');
                  }

                  // Allow non-registration fields to have the same value as registration number
                  // This restriction has been removed to allow manual entry of registration in make/model fields
                }

                const isNumeric = fieldTitle.toLowerCase().includes('km') ||
                  fieldTitle.toLowerCase().includes('hours') ||
                  fieldTitle.toLowerCase().includes('odometer') ||
                  fieldTitle.toLowerCase().includes('year');

                const isFleetFieldInvalid = invalidFields.includes(`fleet-${fieldId}`);
                const validationTitle = String(fieldTitle || '').replace(/\s*\*\s*$/, '').trim();
                return (
                  <div
                    key={fieldId}
                    style={{
                      width: '100%',
                    }}
                    data-field-id={`fleet-${fieldId}`}
                  >
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>{renderLabel(fieldTitle)}</Text>
                    {isMakeField || isModelField ? (
                      <Input
                        placeholder={`Enter ${fieldTitle.toLowerCase()}`}
                        value={displayValue}
                        onChange={(e) => {
                          removeInvalid(`fleet-${fieldId}`);
                          // Update the field value
                          updateItemResponse(fieldId, {
                            text: e.target.value,
                            // Create or update fleetInfo for vehicle fields
                            fleetInfo: {
                              ...(fleetInfo || {}),
                              [isMakeField ? 'make' : 'model']: e.target.value
                            }
                          });
                          // Also update selectedVehicle so future auto-fill uses the manual value
                          setSelectedVehicle(prev => prev ? ({
                            ...prev,
                            [isMakeField ? 'make' : 'model']: e.target.value
                          }) : prev);
                          // Keep registration field's fleetInfo in sync with manual make/model edits
                          const regField = templateJson?.templates?.[0]?.FleetInformation?.find(f => {
                            const t = f.title?.toLowerCase() || '';
                            return t.includes('registration') || t.includes('reg no') || t.includes('regno') || t.includes('rego') || t.includes('license plate') || t.includes('number plate');
                          });
                          if (regField?.itemId) {
                            const currentRegResponse = regField.response || {};
                            updateItemResponse(regField.itemId, {
                              fleetInfo: {
                                ...(currentRegResponse.fleetInfo || {}),
                                [isMakeField ? 'make' : 'model']: e.target.value
                              }
                            });
                          }
                        }}
                        style={{
                          width: '100%',
                          backgroundColor: '#fff'
                        }}
                        disabled={!editable}
                        status={isFleetFieldInvalid ? 'error' : undefined}
                      />
                    ) : isNumeric ? (
                      <Input
                        type="number"
                        placeholder={`Enter ${fieldTitle.toLowerCase()}`}
                        value={displayValue}
                        onChange={(e) => {
                          removeInvalid(`fleet-${fieldId}`);
                          const val = e.target.value;
                          const title = fieldTitle.toLowerCase();
                          const fleetPatch = (isVehicleField && fleetInfo) ? {
                            fleetInfo: {
                              ...(fleetInfo || {}),
                              ...(title.includes('hours') ? { currentHours: val } : {}),
                              ...(title.includes('year') ? { year: val } : {}),
                              ...(title.includes('odometer') || title.includes('km') ? { odometer: val, currentKm: val } : {}),
                            }
                          } : {};
                          updateItemResponse(fieldId, {
                            text: val,
                            ...fleetPatch
                          });
                        }}
                        style={{ width: '100%', backgroundColor: '#fff' }}
                        min={0}
                        step={fieldTitle.toLowerCase().includes('hours') ? 0.1 : 1}
                        suffix={fieldTitle.toLowerCase().includes('hours') ? 'hrs' :
                          fieldTitle.toLowerCase().includes('km') ? 'km' : ''}
                        disabled={!editable}
                        status={isFleetFieldInvalid ? 'error' : undefined}
                      />
                    ) : (
                      <Input
                        placeholder={`Enter ${fieldTitle.toLowerCase()}`}
                        value={displayValue}
                        onChange={(e) => {
                          removeInvalid(`fleet-${fieldId}`);
                          const val = e.target.value;
                          const title = fieldTitle.toLowerCase();
                          const fleetPatch = (isVehicleField && fleetInfo) ? {
                            fleetInfo: {
                              ...(fleetInfo || {}),
                              ...(title.includes('vin') ? { vin: val } : {}),
                              ...(title.includes('make') ? { make: val } : {}),
                              ...(title.includes('model') ? { model: val } : {}),
                            }
                          } : {};
                          updateItemResponse(fieldId, {
                            text: val,
                            ...fleetPatch
                          });
                        }}
                        style={{ width: '100%', backgroundColor: '#fff' }}
                        disabled={!editable}
                        status={isFleetFieldInvalid ? 'error' : undefined}
                      />
                    )}
                    {isFleetFieldInvalid && (
                      <div className="ant-form-item-explain ant-form-item-explain-error" style={{ fontSize: '11px', color: '#ff4d4f' }}>
                        * Please enter {validationTitle}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        );
      }
    }

    // For Fleet Information step, don't render individual items as they're now in the main card
    if (currentStep === 0) {
      return null;
    }

    // Ensure response has the color property
    const itemResponse = {
      color: response?.color,
      comment: response?.comment,
      ...response
    };
    const listOptions = (Array.isArray(config.options) ? config.options : []).map(normalizeListOption);

    const isQuestionInvalid = invalidFields.includes(`question-${itemId}`);
    return (
      <Card key={itemId} size="small" style={{ marginBottom: 16, ...(isQuestionInvalid ? { border: '1px solid #ff4d4f', boxShadow: '0 0 0 2px rgba(255, 77, 79, 0.2)' } : {}) }} data-field-id={`question-${itemId}`}>
        <Title level={5} style={{ marginBottom: 16 }}>
          {renderLabel(title, item.required !== false)}
          {itemRequiresImage(item) ? (
            <Text type="danger" style={{ marginLeft: 8, fontSize: 12 }}>
              Image required
            </Text>
          ) : null}
        </Title>

        {type === 'radio' && (
          <div style={{ marginBottom: 16 }}>
            <Radio.Group
              value={response.optionId ?? undefined}
              onChange={(event) => {
                const selectedId = event.target.value;
                const selected = listOptions.find(option => String(option.id) === String(selectedId));
                if (!selected) return;

                const nextResponse = {
                  ...response,
                  optionId: selected.id,
                  text: selected.label,
                  color: selected.color,
                };
                if (['red', 'amber'].includes(selected.color)) {
                  setCurrentBadgeItem({ ...item, response: nextResponse });
                  setBadgeModalVisible(true);
                } else {
                  updateItemResponse(itemId, nextResponse);
                }
              }}
              disabled={!editable}
              style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}
            >
              {listOptions.map(option => (
                <Radio
                  key={String(option.id)}
                  value={option.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 48,
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: 8,
                  }}
                >
                  <DropdownOption option={{ name: option.label, color: option.color }} />
                </Radio>
              ))}
            </Radio.Group>
            {item.required === false && response.optionId && editable ? (
              <Button
                type="link"
                onClick={() => updateItemResponse(itemId, { optionId: null, text: '', color: null })}
                style={{ paddingInline: 0, marginTop: 8 }}
              >
                Clear selection
              </Button>
            ) : null}
            <TextArea
              rows={2}
              placeholder="Add a comment (required for red/amber status)"
              value={typeof response.comment === 'string' ? response.comment : ''}
              onChange={(event) => updateItemResponse(itemId, { comment: String(event.target.value || '') })}
              style={{ width: '100%', marginTop: 16 }}
              disabled={!editable}
            />
          </div>
        )}

        {type === 'checkbox' && (
          <div style={{ marginBottom: 16 }}>
            <Checkbox.Group
              value={
                response.skipped === true
                  ? []
                  : Array.isArray(response.optionIds) ? response.optionIds : []
              }
              onChange={(selectedIds) => {
                const selectedIdSet = new Set(selectedIds.map(value => String(value)));
                const selected = listOptions.filter(option => selectedIdSet.has(String(option.id)));
                const checklist = getChecklistResult(
                  listOptions,
                  selected.map(option => option.id)
                );
                const nextResponse = {
                  ...response,
                  optionIds: selected.map(option => option.id),
                  skipped: false,
                  text: selected.map(option => option.label).join(', '),
                  color: checklist.color,
                };
                updateItemResponse(itemId, nextResponse);
              }}
              disabled={!editable || response.skipped === true}
              style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}
            >
              {listOptions.map(option => {
                const checked =
                  response.skipped !== true
                  && Array.isArray(response.optionIds)
                  && response.optionIds.some(id => String(id) === String(option.id));
                return (
                  <Checkbox
                    key={String(option.id)}
                    value={option.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: 48,
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: 8,
                      marginInlineStart: 0,
                    }}
                  >
                    <DropdownOption
                      option={{
                        name: option.label,
                        color: response.skipped === true ? 'gray' : checked ? 'green' : 'red'
                      }}
                    />
                  </Checkbox>
                );
              })}
            </Checkbox.Group>
            {item.required === false && editable ? (
              <Button
                type="link"
                onClick={() => {
                  if (response.skipped === true) {
                    updateItemResponse(itemId, {
                      skipped: false,
                      optionIds: [],
                      text: '',
                      color: listOptions.length > 0 ? 'red' : null,
                    });
                  } else {
                    updateItemResponse(itemId, {
                      skipped: true,
                      optionIds: [],
                      text: 'Skipped',
                      color: null,
                    });
                  }
                }}
                style={{ paddingInline: 0, marginTop: 8 }}
              >
                {response.skipped === true ? 'Resume checklist' : 'Skip / Not applicable'}
              </Button>
            ) : null}
            {response.skipped === true && !editable ? (
              <Text type="secondary">Skipped / Not applicable</Text>
            ) : null}
            <TextArea
              rows={2}
              placeholder="Add a comment (required when any checklist item is unchecked)"
              value={typeof response.comment === 'string' ? response.comment : ''}
              onChange={(event) => updateItemResponse(itemId, { comment: String(event.target.value || '') })}
              style={{ width: '100%', marginTop: 16 }}
              disabled={!editable}
            />
          </div>
        )}

        {type === 'dropdown' && (
          <div style={{ marginBottom: 16 }}>
            <Select
              placeholder="Select an option"
              value={
                response.optionId
                || (config.options || []).find(o => (
                  (o.id && String(o.id) === String(response.text))
                  || (o.name && o.name === response.text)
                  || (o.label && o.label === response.text)
                ))?.id
                || undefined
              }
              onChange={(val) => {
                const selectedId = val;

                const options = Array.isArray(config.options) ? config.options : [];
                const match = options.find((o) => {
                  const id = o?.id ?? o?.Id ?? o?.value ?? o?.Value ?? o?.optionId ?? o?.OptionId;
                  return String(id) === String(selectedId);
                });

                const newColor = match ? (match.color ?? match.colour ?? match.Color ?? match.Colour ?? null) : null;
                const selectedText = match
                  ? (match.name ?? match.Name ?? match.label ?? match.Label ?? match.text ?? match.Text ?? String(selectedId || ''))
                  : String(selectedId || '');

                if (['red', 'amber'].includes((newColor || '').toLowerCase())) {
                  setCurrentBadgeItem({
                    ...item,
                    response: {
                      ...item.response,
                      color: newColor,
                      optionId: selectedId,
                      text: String(selectedText || ''),
                    }
                  });
                  setBadgeModalVisible(true);
                } else {
                  updateItemResponse(itemId, {
                    optionId: selectedId,
                    color: newColor,
                    text: String(selectedText || ''),
                  });
                }
              }}
              options={(config.options || []).map((o) => {
                const id = o.id ?? o.Id ?? o.value ?? o.Value ?? o.optionId ?? o.OptionId;
                const name = o.name ?? o.Name ?? o.label ?? o.Label ?? o.text ?? o.Text ?? String(id);
                const color = o.color ?? o.colour ?? o.Color ?? o.Colour ?? null;
                const normalized = { id, name, color };
                return {
                  label: <DropdownOption option={normalized} title={title} />,
                  value: id,
                  option: normalized,
                };
              })}
              style={{ width: '100%' }}
              optionLabelProp="label"
              disabled={!editable}
            />

            {/* Comment Section for Dropdown with colored options */}

            <div style={{ marginTop: 16 }}>
              <TextArea
                rows={2}
                placeholder="Add a comment (required for red/amber status)"
                value={typeof response.comment === 'string' ? response.comment : ''}
                onChange={(e) => updateItemResponse(itemId, { comment: String(e.target.value || '') })}
                style={{ width: '100%', marginBottom: 16 }}
                disabled={!editable}
              />
            </div>

          </div>
        )}

        {type === 'text' && (
          <div style={{ marginBottom: 16 }}>
            {config.multiline ? (
              <TextArea
                rows={3}
                placeholder={config.placeholder || 'Enter text'}
                value={typeof response.text === 'string' ? response.text : ''}
                onChange={(e) => updateItemResponse(itemId, { text: String(e.target.value || '') })}
                style={{ width: '100%' }}
                disabled={!editable}
              />
            ) : (
              <Input
                placeholder={config.placeholder || 'Enter text'}
                value={typeof response.text === 'string' ? response.text : ''}
                onChange={(e) => updateItemResponse(itemId, { text: String(e.target.value || '') })}
                style={{ width: '100%' }}
                disabled={!editable}
              />
            )}
          </div>
        )}

        {type === 'badge' && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <BadgeSelector
                value={itemResponse.color}
                onChange={(val) => handleBadgeChange(item, val)}
                enumColors={config.colorEnum || ['red', 'amber', 'green']}
                disabled={!editable}
              />
            </div>
            <TextArea
              rows={2}
              placeholder="Add a comment (required for red/amber status)"
              value={typeof response.comment === 'string' ? response.comment : ''}
              onChange={(e) => updateItemResponse(itemId, { comment: String(e.target.value || '') })}
              style={{ width: '100%', marginBottom: 16 }}
              disabled={!editable}
            />

            {/* Image Upload Section */}
            <Divider style={{ margin: '16px 0' }} />
            <div style={{ marginTop: 8 }}>
              <Text strong>Images{itemRequiresImage(item) ? ' (required)' : ''}</Text>{processingImages && <span style={{ marginLeft: 8 }}><Spin size="small" /> <Text type="secondary" style={{ fontSize: 12 }}>Compressing...</Text></span>}
              <Upload
                listType="picture-card"
                multiple
                accept="image/*,android/allowCamera"
                beforeUpload={handleBeforeUploadImage}
                itemRender={imageItemRender}
                fileList={mergeImageLists(serverFileMap[itemId], filesMap[itemId])}
                onChange={({ fileList }) => onFilesChange(itemId, fileList)}
                onPreview={handlePreview}
                onRemove={async (file) => {
                  if (file.isServer || file.blobPath) {
                    const success = await handleRemove(file);
                    if (success) {
                      // Remove from serverFileMap
                      const updatedServerFiles = { ...serverFileMap };
                      const blobPathToRemove = file?.blobPath;
                      if (updatedServerFiles[itemId]) {
                        updatedServerFiles[itemId] = updatedServerFiles[itemId].filter((f) => {
                          if (blobPathToRemove && f?.blobPath) return f.blobPath !== blobPathToRemove;
                          return f?.uid !== file.uid;
                        });
                        setServerFileMap(updatedServerFiles);

                        // Also remove any local cached blob copy for this server image
                        if (blobPathToRemove) {
                          setFilesMap((prev) => {
                            const next = { ...(prev || {}) };
                            if (next[itemId]) {
                              next[itemId] = (next[itemId] || []).filter((f) => f?.blobPath !== blobPathToRemove);
                            }
                            return next;
                          });
                        }

                        // Check if this was the last image and badge is red/amber
                        const nextLocal = blobPathToRemove
                          ? (filesMap[itemId] || []).filter((f) => f?.blobPath !== blobPathToRemove)
                          : (filesMap[itemId] || []);
                        const totalImages = mergeImageLists(updatedServerFiles[itemId], nextLocal).length;
                        const currentItem = templateJson?.templates?.[0]?.InspectionQuestions?.find(q => q.itemId === itemId);
                        const badgeColor = currentItem?.response?.color;

                        if (totalImages === 0 && ['red', 'amber'].includes(badgeColor)) {
                          // Reset the badge selection
                          updateItemResponse(itemId, {
                            ...currentItem.response,
                            color: null,
                            comment: currentBadgeItem?.response?.comment || ''
                          });
                        }
                      }
                    }
                    return success;
                  } else {
                    // For non-server files, check if this is the last image after removal
                    const updatedFiles = (filesMap[itemId] || []).filter(f => f.uid !== file.uid);
                    setFilesMap((prev) => {
                      const next = { ...(prev || {}) };
                      next[itemId] = updatedFiles;
                      return next;
                    });
                    const totalImages = mergeImageLists(serverFileMap[itemId], updatedFiles).length;
                    const currentItem = templateJson?.templates?.[0]?.InspectionQuestions?.find(q => q.itemId === itemId);
                    const badgeColor = currentItem?.response?.color;

                    if (totalImages === 0 && ['red', 'amber'].includes(badgeColor)) {
                      // Only reset the color, keep the comment
                      updateItemResponse(itemId, {
                        ...currentItem.response,
                        color: null
                      });
                    }

                    return true;
                  }
                }}
                style={{ margin: '8px 0 0 0', width: '48px', height: '48px' }}
                disabled={!editable}
              >
                <div>
                  <UploadOutlined />
                </div>
              </Upload>
              {(() => {
                const totalCount = mergeImageLists(serverFileMap[itemId], filesMap[itemId]).length;

                return totalCount > 0 ? (
                  <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                    {totalCount} image{totalCount !== 1 ? 's' : ''} attached
                  </Text>
                ) : null;
              })()}
            </div>
          </div>
        )}

        {!['badge', 'text', 'dropdown', 'signature', 'radio', 'checkbox'].includes(type) && (
          <Text type="secondary">This inspection item is not supported by this app version.</Text>
        )}

        {type !== 'badge' && (
          <div>
            <Divider style={{ margin: '16px 0' }} />
            <div>
              <Text strong>Images{itemRequiresImage(item) ? ' (required)' : ''}</Text>{processingImages && <span style={{ marginLeft: 8 }}><Spin size="small" /> <Text type="secondary" style={{ fontSize: 12 }}>Compressing...</Text></span>}
              <Upload
                listType="picture-card"
                multiple
                accept="image/*,android/allowCamera"
                beforeUpload={handleBeforeUploadImage}
                itemRender={imageItemRender}
                fileList={mergeImageLists(serverFileMap[itemId], filesMap[itemId])}
                onChange={({ fileList }) => onFilesChange(itemId, fileList)}
                onPreview={handlePreview}
                onRemove={async (file) => {
                  if (file.isServer || file.blobPath) {
                    const success = await handleRemove(file);
                    if (success) {
                      // Remove from serverFileMap
                      const updatedServerFiles = { ...serverFileMap };
                      const blobPathToRemove = file?.blobPath;
                      if (updatedServerFiles[itemId]) {
                        updatedServerFiles[itemId] = updatedServerFiles[itemId].filter((f) => {
                          if (blobPathToRemove && f?.blobPath) return f.blobPath !== blobPathToRemove;
                          return f?.uid !== file.uid;
                        });
                        setServerFileMap(updatedServerFiles);

                        // Also remove any local cached blob copy for this server image
                        if (blobPathToRemove) {
                          setFilesMap((prev) => {
                            const next = { ...(prev || {}) };
                            if (next[itemId]) {
                              next[itemId] = (next[itemId] || []).filter((f) => f?.blobPath !== blobPathToRemove);
                            }
                            return next;
                          });
                        }

                        // Check if this was the last image and badge is red/amber
                        const nextLocal = blobPathToRemove
                          ? (filesMap[itemId] || []).filter((f) => f?.blobPath !== blobPathToRemove)
                          : (filesMap[itemId] || []);
                        const totalImages = mergeImageLists(updatedServerFiles[itemId], nextLocal).length;
                        const currentItem = templateJson?.templates?.[0]?.InspectionQuestions?.find(q => q.itemId === itemId);
                        const badgeColor = currentItem?.response?.color;

                        if (totalImages === 0 && ['red', 'amber'].includes(badgeColor)) {
                          // Only reset the color, keep the comment
                          updateItemResponse(itemId, {
                            ...currentItem.response,
                            color: null
                          });
                        }
                      }
                    }
                    return success;
                  } else {
                    // For non-server files, check if this is the last image after removal
                    const updatedFiles = (filesMap[itemId] || []).filter(f => f.uid !== file.uid);

                    setFilesMap((prev) => {
                      const next = { ...(prev || {}) };
                      next[itemId] = updatedFiles;
                      return next;
                    });
                    const totalImages = mergeImageLists(serverFileMap[itemId], updatedFiles).length;
                    const currentItem = templateJson?.templates?.[0]?.InspectionQuestions?.find(q => q.itemId === itemId);
                    const badgeColor = currentItem?.response?.color;

                    if (totalImages === 0 && ['red', 'amber'].includes(badgeColor)) {
                      // Only reset the color, keep the comment
                      updateItemResponse(itemId, {
                        ...currentItem.response,
                        color: null
                      });
                    }

                    return true;
                  }
                }}
                style={{ margin: '8px 0 0 0', width: '48px', height: '48px' }}
                disabled={!editable}
              >
                <div>
                  <UploadOutlined />
                </div>
              </Upload>
              {(() => {
                const totalCount = mergeImageLists(serverFileMap[itemId], filesMap[itemId]).length;
                return totalCount > 0 ? (
                  <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                    {totalCount} image{totalCount !== 1 ? 's' : ''} attached
                  </Text>
                ) : null;
              })()}
            </div>
          </div>
        )}

        {type === 'signature' && (
          <>
            {/* Display existing signature images */}

            <SignatureModalPad
              initialImage={(() => {
                const imageEntry = itemResponse?.images?.find(img => img.fieldKey === item.itemId);
                return imageEntry?.imageUrls?.[0] || null;
              })()}
              // disabled={!editable || (filesMap[item.itemId]?.length > 0 || serverFileMap[item.itemId]?.length > 0)}
              onSave={(base64) => {
                // Convert base64 to blob and add to upload system
                fetch(base64)
                  .then(res => res.blob())
                  .then(blob => {
                    // Create a file object with proper name
                    const file = new File([blob], `signature-${item.itemId}-${Date.now()}.png`, {
                      type: 'image/png'
                    });

                    // Create a unique ID for this file
                    const uid = `signature-${item.itemId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

                    // Create file object in format expected by upload system
                    const fileObj = {
                      uid: uid,
                      name: `signature-${item.itemId}-${Date.now()}.png`,
                      status: 'done',
                      originFileObj: file,
                      blob: blob,
                    };

                    const existingFiles = filesMap[item.itemId] || [];
                    onFilesChange(item.itemId, [...existingFiles, fileObj]);
                  })
                  .catch(error => {
                    console.error('Error converting signature to file:', error);
                  });
              }}
              onDelete={() => {
                onFilesChange(item.itemId, []);
              }}
            // hasExistingSignature={(filesMap[item.itemId]?.length > 0 || serverFileMap[item.itemId]?.length > 0)}
            />
          </>
        )}

      </Card>
    );
  };

  // Effect to force re-render when selectedVehicle changes
  useEffect(() => {
    // This will trigger a re-render when selectedVehicle changes
    // which will cause hasUnselectedFields to be recalculated
  }, [selectedVehicle]);

  // Check if any required fields are unselected
  const hasUnselectedFields = useMemo(() => {
    if (currentStep === 0) {
      // If no vehicles are available for the selected company, enable Next button
      if (selectedCompany && vehicles.length === 0) {
        return false;
      }
      // Otherwise, require a vehicle to be selected
      return !selectedVehicle?.registration;
    }

    // For other steps, check the form validation
    return inspectionQuestions.some(item => {
      const isRequired = item.required !== false;
      if (!isRequired) return false;

      if (item.type === 'badge' && !item.response?.color) {
        return true;
      }
      if (item.type === 'dropdown' && !item.response?.optionId) {
        return true;
      }
      if (item.type === 'radio' && !item.response?.optionId) {
        return true;
      }
      if (item.type === 'checkbox') return false;
      if (item.type === 'text' && !item.response?.text) {
        return true;
      }
      return false;
    });
  }, [currentStep, selectedVehicle, inspectionQuestions, selectedCompany, vehicles.length]);

  const validateFleetInformationStep = () => {
    const newInvalidFields = [];

    if (!(selectedCompany || propCompanyId)) {
      newInvalidFields.push('company');
    }

    const registration = String(selectedVehicle?.registration || '').trim();
    const fleetNumber = String(selectedVehicle?.fleet ?? '').trim();

    if (vehicles.length > 0 && !registration) {
      newInvalidFields.push('vehicle');
    }

    if (!fleetNumber) {
      newInvalidFields.push('fleet-number');
    }

    if (!registration) {
      newInvalidFields.push('truck-registration');
    }

    const fleetInfoFields = templateJson?.templates?.[0]?.FleetInformation || [];
    const excludedFields = ['registration', 'reg', 'number plate', 'license plate', 'fleet', 'fleet number'];
    const displayFields = fleetInfoFields.filter((field) => {
      const fieldTitle = field.title?.toLowerCase() || '';
      return !excludedFields.some((excluded) => fieldTitle.includes(excluded));
    });

    const registrationField = fleetInfoFields.find((item) => {
      const t = item?.title?.toLowerCase() || '';
      return t.includes('registration') || t.includes('reg no') || t.includes('regno') || t.includes('rego') || t.includes('license plate') || t.includes('number plate');
    });

    let fleetInfo = selectedVehicle
      ? {
        reg: selectedVehicle.registration || '',
        registration: selectedVehicle.registration || '',
        fleet: selectedVehicle.fleet || '',
        make: selectedVehicle.make || '',
        model: selectedVehicle.model || '',
        year: selectedVehicle.year || '',
        vin: selectedVehicle.vin || '',
        currentHours: selectedVehicle.currentHours ?? 0,
        currentKm: (selectedVehicle.odometer ?? selectedVehicle.currentKm) ?? 0,
        odometer: selectedVehicle.odometer ?? 0,
      }
      : null;

    if (!fleetInfo) {
      fleetInfo = registrationField?.response?.fleetInfo;
    }

    if (!fleetInfo && selectedTruck) {
      fleetInfo = {
        reg: selectedTruck.registration || selectedTruck.reg || '',
        registration: selectedTruck.registration || selectedTruck.reg || '',
        fleet: selectedTruck.fleet || '',
        make: selectedTruck.make || selectedTruck._original?.make || '',
        model: selectedTruck.model || selectedTruck._original?.model || '',
        year: selectedTruck.year || selectedTruck._original?.year || '',
        vin: selectedTruck._original?.vin || '',
        currentHours: selectedTruck.currentHours ?? 0,
        currentKm: selectedTruck.currentKm ?? 0,
      };
    }

    displayFields.forEach((field) => {
      const fieldId = field.itemId;
      if (!fieldId) return;

      const fieldTitle = String(field.title || '');
      const titleLower = fieldTitle.toLowerCase();
      const fieldResponse = field.response || {};
      const rawText = fieldResponse.text;
      const fieldValue = rawText === null || rawText === undefined ? '' : String(rawText);

      let effectiveValue = fieldValue;

      const isEffectivelyEmptyString = typeof effectiveValue === 'string' && effectiveValue.trim().length === 0;
      if (isEffectivelyEmptyString && fleetInfo) {
        if (titleLower.includes('make')) {
          effectiveValue = fleetInfo.make;
        } else if (titleLower.includes('model')) {
          effectiveValue = fleetInfo.model;
        } else if (titleLower.includes('year')) {
          effectiveValue = fleetInfo.year;
        } else if (titleLower.includes('vin') || titleLower.includes('chassis')) {
          effectiveValue = fleetInfo.vin;
        } else if (titleLower.includes('hours')) {
          effectiveValue = fleetInfo.currentHours;
        } else if (titleLower.includes('odometer') || titleLower.includes('km') || titleLower.includes('kilometers') || titleLower.includes('mileage')) {
          effectiveValue = (fleetInfo.odometer ?? fleetInfo.currentKm);
        }
      }

      const normalized = effectiveValue === null || effectiveValue === undefined ? '' : String(effectiveValue);
      const isMissing = normalized.trim().length === 0;

      if (isMissing) {
        newInvalidFields.push(`fleet-${fieldId}`);
      }
    });

    const allInvalidFields = [...new Set(newInvalidFields)];
    setInvalidFields(allInvalidFields);

    if (allInvalidFields.length > 0) {
      message.error('Please complete all required Fleet Information fields');
      scrollToFirstInvalidField(allInvalidFields[0]);
      return false;
    }

    setInvalidFields([]);
    return true;
  };

  const handleNext = () => {
    // When leaving Fleet Information step, persist a draft snapshot immediately
    if (currentStep === 0) {
      const snapshot = buildDraftSnapshot();
      if (snapshot) {
        saveInspectionDraft(snapshot).catch((e) => {
          console.error('Failed to save local inspection draft on Next:', e);
        });
      }
    }
    setCurrentStep(prev => prev + 1);
  };

  const handlePrev = () => {
    setCurrentStep(prev => prev - 1);
  };

  const isLastStep = currentStep === 1; // 0: Fleet Info, 1: Inspection Questions

  // Determine if any user input has been added to allow saving a draft
  const hasAnyInput = useMemo(() => {
    const tpl = templateJson?.templates?.[0];
    const hasResponsesIn = (arr) => (arr || []).some((f) => {
      const r = f?.response || {};
      if (typeof r.text === 'string' && r.text.trim().length > 0) return true;
      if (r.optionId) return true;
      if (Array.isArray(r.optionIds) && r.optionIds.length > 0) return true;
      if (r.skipped === true) return true;
      if (r.color) return true;
      if (typeof r.comment === 'string' && r.comment.trim().length > 0) return true;
      if (Array.isArray(r.imageIds) && r.imageIds.length > 0) return true;
      if (typeof r.__serverImageCount === 'number' && r.__serverImageCount > 0) return true;
      return false;
    });
    const hasVehicle = !!selectedVehicle?.registration || !!selectedVehicle?.fleet || !!selectedVehicle?.vin;
    const hasCompany = !!(selectedCompany || propCompanyId);
    const hasLocalFiles = Object.values(filesMap || {}).some((list) => (list || []).length > 0);
    const hasServerFiles = Object.values(serverFileMap || {}).some((list) => (list || []).length > 0);
    const hasResponses = tpl ? (hasResponsesIn(tpl.FleetInformation) || hasResponsesIn(tpl.InspectionQuestions)) : false;
    return hasCompany || hasVehicle || hasResponses || hasLocalFiles || hasServerFiles;
  }, [templateJson, selectedVehicle, selectedCompany, propCompanyId, filesMap, serverFileMap]);

  return (
    <div style={{ padding: 8, paddingBottom: 'calc(var(--bottom-nav-height, 64px) + env(safe-area-inset-bottom) + 24px)' }}>
      {/* Upload Summary Modal */}
      <Modal
        title="File Selection Summary"
        open={uploadSummaryModal.visible}
        onOk={() => setUploadSummaryModal({ visible: false, valid: [], invalid: [] })}
        onCancel={() => setUploadSummaryModal({ visible: false, valid: [], invalid: [] })}
        okText="OK"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={600}
        style={{ top: 50 }}
        bodyStyle={{
          maxHeight: 400,
          overflowY: 'auto',
          padding: 16
        }}
      >
        {uploadSummaryModal.valid.length > 0 && (
          <div style={{
            marginBottom: 16,
            padding: 12,
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 6
          }}>
            <p style={{ margin: 0, marginBottom: 8, fontWeight: 600, color: '#52c41a' }}>
              ✓ Files that will be added:
            </p>
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              {uploadSummaryModal.valid.map((name, i) => (
                <li key={i} style={{ color: '#389e0d' }}>{name}</li>
              ))}
            </ul>
          </div>
        )}
        {uploadSummaryModal.invalid.length > 0 && (
          <div style={{
            padding: 12,
            backgroundColor: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: 6
          }}>
            <p style={{ margin: 0, marginBottom: 8, fontWeight: 600, color: '#ff4d4f' }}>
              ✗ Files that will not be added (not recognised image formats):
            </p>
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              {uploadSummaryModal.invalid.map((name, i) => (
                <li key={i} style={{ color: '#cf1322' }}>{name}</li>
              ))}
            </ul>
          </div>
        )}
      </Modal>

      {/* Full-screen overlay when compressing many images at once */}
      <LoadingOverlay
        isLoading={bulkCompressionProgress.total >= BULK_COMPRESSION_OVERLAY_THRESHOLD}
        icon={<CameraOutlined />}
        text={`Compressing images (${bulkCompressionProgress.total > 0
          ? Math.round((bulkCompressionProgress.done / bulkCompressionProgress.total) * 100)
          : 0
          }%)`
        }
      />

      {renderBadgeModal()}
      <div style={{ marginBottom: 16 }}>
        <Steps current={currentStep}>
          <Steps.Step title="Fleet Information" />
          <Steps.Step title="Inspection Questions" />
        </Steps>
      </div>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changedValues, allValues) => {
          try {
            const tpl = templateRef.current;
            const fleetInfo = tpl?.templates?.[0]?.FleetInformation;
            if (!Array.isArray(fleetInfo) || !changedValues || typeof changedValues !== 'object') return;

            Object.entries(changedValues).forEach(([fieldId, value]) => {
              if (!fieldId) return;
              const item = fleetInfo.find((x) => String(x?.itemId) === String(fieldId));
              if (!item) return;

              // Persist into templateJson so it survives reload and is included in local snapshots / API saves.
              const nextText = value === undefined || value === null ? '' : String(value);
              const currentText = item?.response?.text === undefined || item?.response?.text === null ? '' : String(item.response.text);
              if (nextText === currentText) return;

              updateItemResponse(fieldId, {
                text: nextText,
                optionId: nextText,
              });
            });
          } catch (e) {
            console.error('Failed to persist FleetInformation form values:', e);
          }
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {currentItems.map(renderItem)}
          <div className="inspection-actions">
            <div className="inspection-actions-left">
              {/* Back button to navigate to inspections list */}
              {!editable && (
                <Button
                  type="default"
                  onClick={() => navigate(-1)}
                  style={{ marginRight: 8 }}
                >
                  Back
                </Button>
              )}
            </div>

            <div className="inspection-actions-right">
              {/* Previous button on Inspection Questions step */}
              {currentStep > 0 && (
                <Button
                  type="primary"
                  onClick={handlePrev}
                  style={{ marginRight: 8 }}
                >
                  Previous
                </Button>
              )}

              {/* Save Draft is always available (when editable) */}
              {editable && (
                <Button
                  onClick={() => submitInternal(false)}
                  loading={isSubmittingDraft}
                  disabled={!hasAnyInput || submitting}
                  style={{ minWidth: 110, marginRight: 8 }}
                >
                  Save Draft
                </Button>
              )}

              {/* Step control / Submit */}
              {!isLastStep ? (
                <Button
                  type="primary"
                  onClick={handleNext}
                >
                  Next
                </Button>
              ) : (
                editable && (
                  <Button
                    type="primary"
                    onClick={() => submitInternal(true)}
                    loading={isSubmittingCompleted}
                    disabled={!hasAnyInput || submitting}
                    style={{ minWidth: 96 }}
                  >
                    Submit
                  </Button>
                )
              )}
            </div>
          </div>
        </Space>
      </Form>
      {previewImage && (
        <Image
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            src: previewImage,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => { if (!visible) setPreviewImage(''); },
          }}
        />
      )}
    </div>
  );
};

export default InspectionFormBuilder;

